// XYZ Ventures underwriting model engine — pure functions, no side effects.

export const STAGES = [
  "Sourced",
  "Screening",
  "Underwriting",
  "LOI Submitted",
  "Diligence",
  "IC Approval",
  "Closed",
  "Passed",
];

export const DEMO_DEAL = {
  dealName: "Project Cypress",
  companyName: "Green Horizon Landscaping LLC",
  industry: "Landscaping & grounds maintenance",
  analystName: "XYZ Ventures",
  analystContact: "Ike Bams",
  analystEmail: "",
  analystPhone: "",
  prepDate: "",
  sellerName: "Robert Vance",
  address: "McKinney, TX",
  brokerListingId: "DFW-2291",
  brokerName: "Lone Star Business Advisors",
  brokerPhone: "(972) 555-0148",
  brokerEmail: "deals@lonestaradvisors.com",
  dealType: "Asset Sale",
  yearEstablished: 2009,
  employeeCount: 14,
  reasonForSale: "Owner retirement",

  askPrice: 500000,
  sde: 165000,
  officerSalaryAddback: 95000,
  reVal: 0,
  nwc: 15000,
  ffe: 60000,
  goodwill: 440000,

  slbOn: false,
  slbProceeds: 0,

  downPct: 15,
  loanYrs: 10,
  rate: 11,

  snAmt: 75000,
  snRate: 8,
  snYrs: 5,

  rollPct: 0,

  sqft: 4000,
  rentRate: 12,
  rentEsc: 3,
  leaseYrs: 10,
  leaseType: "NNN",

  sdeGrowth: 3,
  salary: 65000,
  capex: 10000,
  hold: 5,
  exitMult: 3.0,
  holdIndefinite: false,

  upGrowth: 6,
  upExitDelta: 0.75,
  downGrowth: -2,
  downExitDelta: 0.75,

  mgmtFeePct: 0,
  mgmtFeeBasis: "EBITDA",
  mgmtFeeFreq: "Quarterly",

  lpEquity: 0,
  prefReturn: 8,
  gpCallPct: 0,
  lpSplit: 80,
  moicStepUp: 2.0,
  gpPromoteStep: 30,
  moicStepUp2: 3.0,
  gpPromoteStep2: 40,
  moicTarget: 2.5,
  sellerCommonPct: 0,
  liqPrefX: 1,
  liqPrefType: "nonpart",

  grossMarginPct: 42,
  ttmRevenue: 1250000,
  transExpenses: 15000,

  // ---- Back-lever refinance (Year-N recap of the platform + owned add-ons) ----
  blOn: false,
  blYear: 2,
  blMult: 4,
  blDscr: 1.25,
  blRate: null,
  blYrs: null,
  blUse: "addons",     // "addons" (recycle into add-ons, excess to LPs) | "distribute" (all to LPs)

  viewScope: "platform", // deal view chosen on Assumptions: this deal only / consolidated roll-up / a single add-on
  // ---- Distribution policy (payout ratio vs cash kept on the balance sheet) ----
  cashPolicy: "distribute", // "distribute" (100% payout) | "retain" (payout = 100 − retainPct)
  retainPct: 0,             // % of after-debt free cash flow kept on the balance sheet
  retainThruYr: 0,          // retain through this year, then distribute (0 = until exit)
  retainUse: "addons",      // "addons" (deployable war chest) | "balance" (cash held, returned at exit)
  retainFrom: "close",      // when retention starts: "close" | "year" (retainFromYr) | "ebitda" (retainFromEbitda)
  retainFromYr: 3,
  retainFromEbitda: 5000000,
  sfOn: false,              // self-funded add-on engine: buy add-ons out of retained cash once retention starts
  sfEbitda: 0,              // 0 = use the average add-on profile from the Roll-Up settings
  sfMult: 0,
  sfDebtPct: null,
  sfUpliftPct: 0,           // synergy / shared-overhead margin uplift on each self-funded add-on
  sfMaxPerYr: 1,
  sfBuffer: 0,              // minimum cash kept on the balance sheet before buying
  sfStartYr: 0,             // first year the war chest may buy (0 = the year retention starts)
  sfOverrides: {},          // per add-on: { "1": { ebitda, multiple, year } }
  warChestUse: "extra",     // "extra" = LP capital still called in full, war chest buys more add-ons
  lpFunding: "close",       // "close" = LPs fund the whole commitment at close (add-on equity held in escrow) | "calls" = called as add-ons close


  // ---- Working Capital drivers (feed nwc → Assumptions Uses & Sources/Uses) ----
  wcMethod: "days",     // "days" | "percent" | "manual"
  dso: 35,              // days sales outstanding (A/R)
  dio: 30,              // days inventory outstanding
  dpo: 25,              // days payable outstanding (A/P)
  wcPctRevenue: 8,      // NWC as % of TTM revenue (percent method)
  nwcFundedPct: 100,    // % of the NWC target funded in the deal Uses (rest delivered by seller at close)

  acqFeePct: 0,
  ddCost: 0,
  closingCostPct: 0,
  equityFeePct: 0,
  debtFeePct: 0,

  historicalYears: [
    { year: "2021", revenue: 1080000, gm: 40, netIncome: 62000, sde: 138000 },
    { year: "2022", revenue: 1165000, gm: 41, netIncome: 71000, sde: 150000 },
    { year: "2023", revenue: 1240000, gm: 42, netIncome: 78000, sde: 160000 },
    { year: "TTM", revenue: 1250000, gm: 42, netIncome: 82000, sde: 165000 },
  ],
  revenueMix: [
    { label: "Commercial Maintenance", value: 55 },
    { label: "Residential", value: 25 },
    { label: "Install / Hardscape", value: 15 },
    { label: "Irrigation", value: 5 },
  ],
};

// ---------- formatting ----------
// Inputs can hand back digit strings ("4.5") — coerce before any numeric formatting so a string never crashes toFixed.
const n_ = (v) => (typeof v === "number" ? (isFinite(v) ? v : 0) : Number(String(v ?? "").replace(/,/g, "")) || 0);
export const fmt$ = (v) => { const n = n_(v); return (n < 0 ? "-$" : "$") + Math.abs(Math.round(n)).toLocaleString("en-US"); };
export const fmtNum = (v) => Math.round(n_(v)).toLocaleString("en-US");
export const fmtPct = (v) => `${n_(v).toFixed(1)}%`;
export const fmtX = (v) => `${n_(v).toFixed(2)}×`;

// Numeric-field guard: any key whose default in the deal model is a number is always stored as a number,
// so string inputs can never leak into the math (string + number concatenates and breaks toFixed downstream).
export const coerceNum = n_;
export const coerceField = (key, value) =>
  (typeof DEMO_DEAL[key] === "number" ? (value === "" || value == null ? 0 : n_(value)) : value);
// Parsers and imports sometimes hand back gross margin as a ratio (0.2557) instead of a percent
// (25.57). No operating business runs a sub-1% gross margin, so treat anything in (0, 1] as a ratio.
export const pctOf = (v) => {
  const n = n_(v);
  return n > 0 && n <= 1 ? n * 100 : n;
};

// Also used for partial patches, so only normalise keys the caller actually supplied — otherwise a
// one-field patch would overwrite untouched fields with zeros.
export const coerceModel = (raw) => {
  const out = { ...(raw || {}) };
  for (const k of Object.keys(out)) {
    if (typeof DEMO_DEAL[k] === "number" && typeof out[k] !== "number") out[k] = n_(out[k]);
  }
  if ("ddPct" in out || "ddCost" in out) {
    if (out.ddPct == null || out.ddPct === "") out.ddPct = ddPctOf(out);
    else if (typeof out.ddPct !== "number") out.ddPct = n_(out.ddPct);
  }
  if ("grossMarginPct" in out) out.grossMarginPct = pctOf(out.grossMarginPct);
  if (Array.isArray(out.historicalYears)) {
    out.historicalYears = out.historicalYears.map((h) => ({ ...h, gm: pctOf(h && h.gm) }));
  }
  return out;
};

// ---------- core amortization ----------
export function amortMonthly(P, annualRatePct, years) {
  if (P <= 0 || years <= 0) return 0;
  const r = annualRatePct / 100 / 12;
  const n = years * 12;
  if (r === 0) return P / n;
  return (P * r) / (1 - Math.pow(1 + r, -n));
}

export function remainingBalance(P, annualRatePct, years, paymentsMade) {
  if (P <= 0) return 0;
  const r = annualRatePct / 100 / 12;
  const n = years * 12;
  const p = Math.min(paymentsMade, n);
  if (r === 0) return Math.max(0, P * (1 - p / n));
  const f = Math.pow(1 + r, n);
  const fp = Math.pow(1 + r, p);
  return Math.max(0, (P * (f - fp)) / (f - 1));
}

// ---------- recapitalization (recap an owned platform, bring in new LPs, start a roll-up) ----------
export const isRecap = (M) => M.txnType === "recap";
export const DEFAULT_RECAP = { parentName: "", platformId: null, recapMult: 5, existingDebt: 0, newDebt: 0, cashOutPct: 50, warChest: 0, origEquity: 0, origYears: 3, legacyDist: 0 };
export const recapCfg = (M) => ({ ...DEFAULT_RECAP, ...(M.recap || {}) });
export function recapLegacy(M) {
  const R = recapCfg(M);
  const ev = (M.sde || 0) * (Number(R.recapMult) || 0);
  const legacyEquityValue = Math.max(0, ev - (Number(R.existingDebt) || 0));
  const cashOut = legacyEquityValue * (Math.min(100, Math.max(0, Number(R.cashOutPct) || 0)) / 100);
  return { R, ev, legacyEquityValue, cashOut, rolled: legacyEquityValue - cashOut };
}
// New equity before fees (used as the placement-fee basis to avoid a fee↔equity loop).
export function recapNewEquityPreFees(M) {
  const { R, cashOut, rolled } = recapLegacy(M);
  const uses = (Number(R.existingDebt) || 0) + cashOut + (Number(R.warChest) || 0) + (M.transExpenses || 0);
  return Math.max(0, uses - (Number(R.newDebt) || 0) - rolled - (M.snAmt || 0));
}
export function recapModel(M) {
  const L = recapLegacy(M);
  const R = L.R;
  const fees = transCostsTotal(M);
  const existingDebt = Number(R.existingDebt) || 0, warChest = Number(R.warChest) || 0, newDebt = Number(R.newDebt) || 0, sn = M.snAmt || 0;
  const uses = [["Refinance existing debt", existingDebt], ["Legacy investor buyout (cash-out)", L.cashOut], ["Add-on war chest (cash to balance sheet)", warChest],
    ["Transaction expenses", M.transExpenses || 0], ["Transaction costs (fees)", fees]].filter((u) => u[1] > 0.5);
  let totalUses = existingDebt + L.cashOut + warChest + (M.transExpenses || 0) + fees;
  // A sale-leaseback at close funds the recap like debt does: it lands before the LP cheque,
  // so the equity ask drops dollar for dollar (surplus falls through to the balance sheet).
  const slb = M.slbOn ? Math.max(0, Number(M.slbProceeds) || 0) : 0;
  const reVal = slb > 0 ? Math.max(0, Number(M.reVal) || 0) : 0;
  if (reVal > 0.5) { uses.push(["Real estate (bought, then sold to the SLB buyer)", reVal]); totalUses += reVal; }
  const excess = Math.max(0, newDebt + L.rolled + sn + slb - totalUses);
  if (excess > 0.5) { uses.push(["Excess proceeds to balance sheet", excess]); totalUses += excess; }
  const newLp = Math.max(0, totalUses - newDebt - L.rolled - sn - slb);
  const sources = [["SLB proceeds", slb], ["New senior facility", newDebt], ["Seller / legacy note", sn], ["Rolled legacy equity", L.rolled], ["New LP equity", newLp]].filter((u) => u[1] > 0.5);
  const totalSources = sources.reduce((a, u) => a + u[1], 0);
  const orig = Number(R.origEquity) || 0, dist = Number(R.legacyDist) || 0, yrs = Math.max(1, Math.round(Number(R.origYears) || 1));
  const realized = dist + L.cashOut, totalValue = realized + L.rolled;
  const flowsFor = (terminal) => { const f = [-orig]; for (let y = 1; y <= yrs; y++) f.push(dist / yrs + (y === yrs ? terminal : 0)); return f; };
  const irrRealized = orig > 0 ? computeIRR(flowsFor(L.cashOut)) : NaN;
  const irrTotal = orig > 0 ? computeIRR(flowsFor(L.cashOut + L.rolled)) : NaN;
  return { ...L, fees, uses, totalUses, sources, totalSources, newLp, newDebt, slb, warChest, warChestTotal: warChest + excess, excess, existingDebt, orig, dist, yrs, realized, totalValue,
    moicRealized: orig > 0 ? realized / orig : 0, moicTotal: orig > 0 ? totalValue / orig : 0, irrRealized, irrTotal,
    leverage: M.sde > 0 ? newDebt / M.sde : 0, newLpOwnership: newLp + L.rolled > 0 ? newLp / (newLp + L.rolled) : 0 };
}

// ---------- computed ----------
export const downAmt = (M) => (isRecap(M) ? recapNewEquityPreFees(M) : M.askPrice * (M.downPct / 100));
export const loanAmt = (M) => (isRecap(M) ? Math.max(0, Number((M.recap || {}).newDebt) || 0) : Math.max(0, M.askPrice * (1 - M.downPct / 100) - M.snAmt));
export const monthlyPmt = (M) => amortMonthly(loanAmt(M), M.rate, M.loanYrs);
// All-equity deal: no senior facility and no seller paper. A leaseback is a property sale, not debt.
export const isUnlevered = (M) => loanAmt(M) < 1 && (Number(M.snAmt) || 0) < 1;
export const annualDS = (M) => monthlyPmt(M) * 12;
export const snMonthlyPmt = (M) => amortMonthly(M.snAmt, M.snRate, M.snYrs);
export const snAnnualDS = (M) => snMonthlyPmt(M) * 12;

// Max senior loan supportable at a target DSCR (on total debt service incl. seller note).
export function maxLoanAtDSCR(M, target = 1.25) {
  const cf = cfads(M);
  const maxTotalDS = cf / target;
  const maxSeniorDS = Math.max(0, maxTotalDS - snAnnualDS(M));
  const r = M.rate / 100 / 12, n = M.loanYrs * 12;
  const pmtM = maxSeniorDS / 12;
  const maxSeniorLoan = r > 0 ? pmtM * (1 - Math.pow(1 + r, -n)) / r : pmtM * n;
  const requested = loanAmt(M);
  const headroom = maxSeniorLoan - requested;
  return { target, cfads: cf, maxTotalDS, maxSeniorDS, maxSeniorLoan: Math.max(0, maxSeniorLoan), requested, headroom, fits: headroom >= 0 };
}
export const totalAnnualDS = (M) => annualDS(M) + snAnnualDS(M);
export const annualRent = (M) => (M.slbOn ? M.sqft * M.rentRate : 0);

// ---------- transaction costs ----------
export const equityPlacement = (M) => downAmt(M);
export const debtPlacement = (M) => loanAmt(M) + (M.snAmt || 0);
// Due diligence is a % of acquisition cost so it scales with every add-on. Deals saved before the
// change carry a flat `ddCost`, which converts to the equivalent % of their purchase price.
export const ddPctOf = (M) => {
  const p = M && M.ddPct;
  if (p != null && p !== "") return Number(p) || 0;
  const price = (M && M.askPrice) || 0;
  return price ? ((M.ddCost || 0) / price) * 100 : 0;
};

export function transactionCosts(M) {
  const price = M.askPrice || 0;
  const acqFee = ((M.acqFeePct || 0) / 100) * price;
  const ddCost = (ddPctOf(M) / 100) * price;
  const closingCost = ((M.closingCostPct || 0) / 100) * price;
  const equityFee = ((M.equityFeePct || 0) / 100) * equityPlacement(M);
  const debtFee = ((M.debtFeePct || 0) / 100) * debtPlacement(M);
  const total = acqFee + ddCost + closingCost + equityFee + debtFee;
  return { acqFee, ddCost, closingCost, equityFee, debtFee, total };
}

// The same fee schedule charged on every add-on we buy. These dollars ride inside each add-on's
// equity check (funded by the war chest / retained cash / capital call), so they are reported
// separately from the platform's transaction costs and never counted twice.
export function addonFeesFor(M, row) {
  const price = row.price || 0;
  return ((M.acqFeePct || 0) / 100) * price + ((M.closingCostPct || 0) / 100) * price + (ddPctOf(M) / 100) * price
    + ((M.equityFeePct || 0) / 100) * (row.equityBase || 0) + ((M.debtFeePct || 0) / 100) * ((row.debt || 0) + (row.sellerNote || 0));
}
export function addonCosts(M) {
  const rows = addonTableRows(M);
  const sum = (k) => rows.reduce((a, r) => a + (r[k] || 0), 0);
  return { count: rows.length, fees: sum("fees"), nwc: sum("nwc"), price: sum("price"), rows };
}
export const transCostsTotal = (M) => transactionCosts(M).total;

// Add-on seller-note terms: default to the platform note, overridable per roll-up.
export const addonDebtYrs = (M) => {
  const y = M && M.rollup && M.rollup.debtYrs;
  return y == null || y === "" ? (M.loanYrs || 0) : Number(y) || 0;
};
export const addonSnRate = (M) => { const r = (M && M.rollup && M.rollup.snRate); return r == null || r === "" ? (M.snRate || 0) : Number(r) || 0; };
export const addonSnYrs = (M) => { const y = (M && M.rollup && M.rollup.snYrs); return y == null || y === "" ? (M.snYrs || 0) : Number(y) || 0; };

// Add-on working capital: the platform's WC method applied to the add-on's implied revenue,
// funded in cash at each add-on close.
export function addonWC(M, ebitda) {
  const e = Number(ebitda) || 0;
  const ratio = M && M.sde > 0 ? (M.ttmRevenue || 0) / M.sde : 0;
  const rev = e * ratio;
  if (rev <= 0) return 0;
  if ((M.wcMethod || "days") === "manual") {
    const pct = (M.ttmRevenue || 0) > 0 ? (M.nwc || 0) / M.ttmRevenue : 0;
    return Math.round(rev * pct);
  }
  return workingCapital({ ...M, ttmRevenue: rev }).funded;
}

// ---------- working capital ----------
// Builds the net working-capital requirement from its drivers. The resulting
// `netWC` is written back to M.nwc so it flows through the Uses & Sources/Uses.
export function workingCapital(M) {
  const rev = M.ttmRevenue || 0;
  const cogs = rev * (1 - (M.grossMarginPct || 0) / 100);
  const method = M.wcMethod || "days";
  let ar = 0, inventory = 0, ap = 0, netWC = 0;
  if (method === "days") {
    ar = rev * (M.dso || 0) / 365;
    inventory = cogs * (M.dio || 0) / 365;
    ap = cogs * (M.dpo || 0) / 365;
    netWC = ar + inventory - ap;
  } else if (method === "percent") {
    netWC = rev * (M.wcPctRevenue || 0) / 100;
  } else {
    netWC = M.nwc || 0;
  }
  const funded = method === "manual" ? netWC : netWC * ((M.nwcFundedPct ?? 100) / 100);
  const cycleDays = (M.dso || 0) + (M.dio || 0) - (M.dpo || 0);
  return {
    rev, cogs, ar, inventory, ap,
    netWC: Math.round(netWC),
    funded: Math.round(Math.max(0, funded)),
    cycleDays, method,
  };
}


export const nac = (M) => M.askPrice - (M.slbOn ? M.slbProceeds : 0) + transCostsTotal(M);
// Non-recap: the rolled stake comes OUT of the equity cheque, so it can never exceed the down payment
// (a bigger roll than that would leave Sources over-funded and ownership > 100%).
export const rolloverAmt = (M) => (isRecap(M) ? recapLegacy(M).rolled : Math.min(M.askPrice * (M.rollPct / 100), downAmt(M)));
export const rolloverClamped = (M) => !isRecap(M) && M.askPrice * (M.rollPct / 100) > downAmt(M) + 0.5;
// Recap: go-forward equity = new LP equity + rolled legacy equity (fees are added back by exitReturns).
export const buyerCashIn = (M) => (isRecap(M) ? recapTotalEquityExFees(M) : Math.max(0, downAmt(M) - rolloverAmt(M)));
export function recapTotalEquityExFees(M) { const r = recapModel(M); return Math.max(0, r.newLp + r.rolled - (M.transExpenses || 0) - r.fees); }
export const trueEarnings = (M) => M.sde - M.salary;
// Consolidated: the fee is charged on the platform PLUS the add-ons we already own at that point.
// An add-on bought in Year N only enters the fee base (and the earnings) from Year N+1, so the
// Year-1 figures must not carry the full run-rate fee — that is what made DSCR collapse.
const addonsOwnedAt = (M, y) => addonsOf(M).filter((c) => (c.year || 0) < y);
export const addonEbitdaTotal = (M, y = 1) => addonsOwnedAt(M, y).reduce((a, c) => a + (c.ebitda || 0), 0);
export const addonRevenueTotal = (M, y = 1) => { const ratio = M && M.sde > 0 ? (M.ttmRevenue || 0) / M.sde : 0; return addonEbitdaTotal(M, y) * ratio; };
export const mgmtFeeBase = (M, y = 1) => (M.mgmtFeeBasis === "Gross Revenue"
  ? (M.ttmRevenue || 0) + addonRevenueTotal(M, y)
  : trueEarnings(M) + addonEbitdaTotal(M, y));
export const mgmtFee = (M) => Math.max(0, ((M.mgmtFeePct || 0) / 100) * mgmtFeeBase(M));
export const mgmtFeePerPeriod = (M) => mgmtFee(M) / (M.mgmtFeeFreq === "Monthly" ? 12 : 4);
export const cfads = (M) => trueEarnings(M) - M.capex - annualRent(M) - mgmtFee(M);
export const fcfYr1 = (M) => cfads(M) - totalAnnualDS(M);
export const dscrYr1 = (M) => (totalAnnualDS(M) > 0 ? cfads(M) / totalAnnualDS(M) : 0);
export const effectiveHold = (M) => (M.holdIndefinite ? 25 : Math.max(1, M.hold || 5));

// Add-ons (consolidated roll-up view): an add-on acquired in Year N contributes EBITDA & debt service from Year N+1.
// Manual (committed) add-ons plus anything the self-funded engine buys out of retained cash.
const addonsOf = (M) => {
  const manual = (M && M.addons) || [];
  if (!M || !M.sfOn || M._noSelfFund) return manual;
  // With a roll-up attached the engine belongs to the consolidated view only — the platform-only view
  // must never grow its own, different set of self-funded add-ons.
  if (M.rollup && M._scope !== SCOPE_ROLLUP && scopeOptions(M)) return manual;
  return [...manual, ...selfFundedAddons(M)];
};
// Reporting helpers below always read the self-funded plan off the consolidated model so every
// table, rail and tile shows the same add-ons.
const sfScope = (M) => (M && M.rollup && M._scope !== SCOPE_ROLLUP && scopeOptions(M) ? rollupScopedModel(M) : M);

// ---------- back-lever refinance ----------
// In the back-lever year the platform (plus every add-on already owned) is refinanced with one new
// senior facility sized at blMult × EBITDA, capped so total debt service keeps DSCR ≥ blDscr on that
// year's CFADS. Proceeds net of the debt retired are dry powder for the add-on pipeline (Variant B)
// or returned to LPs (Variant A). The new facility's debt service hits FCF from the following year.
export const isBackLever = (M) => !!(M && M.blOn);
export const blRateOf = (M) => (M.blRate == null || M.blRate === "" ? Number((M.rollup || {}).debtRate) || M.rate : Number(M.blRate) || 0);
export const blYrsOf = (M) => (M.blYrs == null || M.blYrs === "" ? M.loanYrs : Number(M.blYrs) || M.loanYrs);
const principalForDS = (ds, ratePct, years) => {
  if (ds <= 0 || years <= 0) return 0;
  const r = ratePct / 100 / 12, n = years * 12;
  return r > 0 ? ((ds / 12) * (1 - Math.pow(1 + r, -n))) / r : (ds / 12) * n;
};
export function backLever(M) {
  if (!isBackLever(M)) return null;
  const H = effectiveHold(M);
  const Y = Math.min(Math.max(1, Math.round(Number(M.blYear) || 2)), Math.max(1, H - 1));
  const r = model5yr({ ...M, blOn: false })[Y - 1];
  if (!r) return null;
  const addons = addonsOf(M);
  const rate = blRateOf(M), yrs = blYrsOf(M), mult = Number(M.blMult) || 0, target = Math.max(1, Number(M.blDscr) || 1.25);
  const ebitda = r.sde_y - M.salary;
  const grossAsk = Math.max(0, ebitda * mult);
  const owned = addons.filter((c) => c.year < Y), kept = addons.filter((c) => c.year >= Y);
  const y1 = Y + 1, snY = (c) => (c.snYrs == null ? M.snYrs : c.snYrs);
  const otherDS = (y1 <= M.snYrs ? snAnnualDS(M) : 0)
    + owned.reduce((a, c) => a + (y1 - c.year <= snY(c) ? c.snDs : 0), 0)
    + kept.reduce((a, c) => a + (y1 > c.year && y1 - c.year <= M.loanYrs ? c.ds : 0) + (y1 > c.year && y1 - c.year <= snY(c) ? c.snDs : 0), 0);
  const maxDS = Math.max(0, r.cfads / target - otherDS);
  const maxFacility = principalForDS(maxDS, rate, yrs);
  const facility = Math.max(0, Math.min(grossAsk, maxFacility));
  const refinanced = remainingBalance(loanAmt(M), M.rate, M.loanYrs, Y * 12) + owned.reduce((a, c) => a + remainingBalance(c.debt, c.rate, M.loanYrs, (Y - c.year) * 12), 0);
  const proceeds = Math.max(0, facility - refinanced);
  const active = proceeds > 0.5;
  const ds = active ? annuity(facility, rate, yrs) : 0;
  const use = M.blUse === "distribute" ? "distribute" : "addons";
  const pipeline = kept.reduce((a, c) => a + (c.equity || 0), 0);
  const extraMode = warChestUseOf(M) === "extra";
  const toReserve = active && use === "addons" ? (extraMode ? proceeds : Math.min(proceeds, pipeline)) : 0;
  const toLp = active ? proceeds - toReserve : 0;
  return { on: true, active, year: Y, ebitda, mult, grossAsk, maxFacility, capped: grossAsk > maxFacility + 0.5, facility, refinanced, refinancedAddons: owned.length,
    proceeds, ds, rate, yrs, use, target, cfads: r.cfads, otherDS, toReserve, toLp, pipeline, extraMode, dscrAfter: ds + otherDS > 0 ? r.cfads / (ds + otherDS) : 0 };
}
// Unallocated proceeds → how much extra add-on EBITDA they could buy at the roll-up terms.
export function blDryPowderCapacity(M, bl) {
  if (!bl || !bl.active || bl.toLp <= 0) return null;
  const R = rollupCfgFor(M);
  const eqShare = Math.max(0.05, 1 - ((R.debtPct || 0) + (R.sellerNotePct || 0) + (R.rolloverPct || 0)) / 100);
  const rows = rollupModelFor(M).rows.filter((x) => x.year > 0);
  const mult = rows.length ? rows.reduce((a, x) => a + x.price, 0) / Math.max(1, rows.reduce((a, x) => a + x.ebitda, 0)) : Number(R.companies?.[0]?.multiple) || 4;
  const price = bl.toLp / eqShare;
  return { cash: bl.toLp, eqShare, mult, price, ebitda: mult > 0 ? price / mult : 0 };
}

// ---------- self-funded add-on engine ----------
// Once retention has switched on (see retainStartYear) the war chest grows every year. As soon as it
// covers the equity on one standard add-on — profile taken from the Roll-Up settings unless overridden —
// the engine buys one (up to sfMaxPerYr a year), keeping sfBuffer of cash back. Bought EBITDA joins the
// consolidated base the following year and compounds with the platform, plus an optional synergy uplift.
export const SF_NAME = "Self-funded add-on";
export const sfProfile = (M) => {
  const R = rollupCfgFor(M);
  const rows = (R.companies || []).filter((c) => Number(c.year) > 0);
  const avgEb = rows.length ? rows.reduce((a, c) => a + (Number(c.ebitda) || 0), 0) / rows.length : 0;
  const avgMult = rows.length ? rows.reduce((a, c) => a + (Number(c.multiple) || 0), 0) / rows.length : 0;
  const ebitda = Number(M.sfEbitda) > 0 ? Number(M.sfEbitda) : avgEb || Math.max(100000, (M.sde || 0) * 0.4);
  const multiple = Number(M.sfMult) > 0 ? Number(M.sfMult) : avgMult || 4;
  const debtPct = M.sfDebtPct == null || M.sfDebtPct === "" ? Number(R.debtPct) || 0 : Number(M.sfDebtPct);
  const snPct = Number(R.sellerNotePct) || 0;
  const roPct = Number(R.rolloverPct) || 0;
  const price = ebitda * multiple;
  const debt = (price * debtPct) / 100;
  const sellerNote = (price * snPct) / 100;
  const rollover = (price * roPct) / 100;
  const nwc = addonWC(M, ebitda);
  const equityBase = Math.max(0, price - debt - sellerNote - rollover);
  const fees = addonFeesFor(M, { price, debt, sellerNote, equityBase });
  const equity = equityBase + nwc + fees;
  const uplift = Math.max(0, Number(M.sfUpliftPct) || 0) / 100;
  return { ebitda, multiple, debtPct, snPct, roPct, price, debt, sellerNote, rollover, nwc, fees, equity, uplift,
    maxPerYr: Math.max(1, Math.round(Number(M.sfMaxPerYr) || 1)), buffer: Math.max(0, Number(M.sfBuffer) || 0) };
};

// A single self-funded add-on, including any per-row override typed into the table (EBITDA / entry multiple).
export const sfRowProfile = (M, p, idx) => {
  const o = ((M && M.sfOverrides) || {})[String(idx)] || {};
  const ebitda = Number(o.ebitda) > 0 ? Number(o.ebitda) : p.ebitda;
  const multiple = Number(o.multiple) > 0 ? Number(o.multiple) : p.multiple;
  const price = ebitda * multiple;
  const debt = (price * p.debtPct) / 100;
  const sellerNote = (price * p.snPct) / 100;
  const rollover = (price * p.roPct) / 100;
  const nwc = addonWC(M, ebitda);
  const equityBase = Math.max(0, price - debt - sellerNote - rollover);
  const fees = addonFeesFor(M, { price, debt, sellerNote, equityBase });
  return { ebitda, multiple, price, debt, sellerNote, rollover, nwc, fees, year: Number(o.year) || 0,
    equity: equityBase + nwc + fees, edited: !!(o.ebitda || o.multiple || o.year) };
};


const SF_CACHE = new Map();
// Cache key = every input the engine can see (fees, WC drivers, recap war chest, funding mode, scope …) —
// a partial key here is exactly how two pages end up showing two different sets of self-funded add-ons.
const SF_SKIP = new Set(["historicalYears", "revenueMix", "notes", "documents", "aiEval", "market", "_noSelfFund"]);
const sfKey = (M) => JSON.stringify(M, (k, v) => (SF_SKIP.has(k) || (k.startsWith("_") && k !== "_scope") ? undefined : v));

export function selfFundedAddons(M) {
  if (!M || !M.sfOn || !isRetain(M) || retainUseOf(M) !== "addons") return [];
  const key = sfKey(M);
  const hit = SF_CACHE.get(key);
  if (hit) return hit;
  const H = effectiveHold(M);
  const p = sfProfile(M);
  const rate = Number(rollupCfgFor(M).debtRate) || M.rate;
  const snR = addonSnRate(M), snY = addonSnYrs(M), dsY = addonDebtYrs(M);
  const out = [];
  // The first buying year: whatever the user typed, else the year retention switches on.
  const manual = Math.round(Number(M.sfStartYr) || 0);
  const start = manual > 0 ? manual : retainStartYear({ ...M, _noSelfFund: true });
  for (let y = Math.max(1, start); y <= H - 1; y++) {
    let bought = 0;
    for (;;) {
      const cand = sfRowProfile(M, p, out.length + 1);
      if (cand.equity <= 0) break;
      if (cand.year && y < cand.year) break;      // this one is pinned to a later year
      // The per-year cap is a pacing default; a row pinned to this year by the user always gets its shot,
      // so two add-ons can be typed into the same year and both buy if the war chest covers them.
      if (bought >= p.maxPerYr && cand.year !== y) break;
      const probe = { ...M, _noSelfFund: true, addons: [...(M.addons || []), ...out] };
      const row = cashPlan(probe).rows.find((r) => r.y === y);
      const avail = Math.max(0, (row ? row.deployableEnd : 0) - p.buffer);
      if (avail < cand.equity) break;
      out.push({ name: `${SF_NAME} ${out.length + 1}`, selfFunded: true, edited: cand.edited, year: y, pinnedYear: cand.year || 0,
        ebitda: cand.ebitda * (1 + p.uplift), ebitdaBought: cand.ebitda, multiple: cand.multiple,
        price: cand.price, debt: cand.debt, sellerNote: cand.sellerNote, rollover: cand.rollover, nwc: cand.nwc, fees: cand.fees,
        equity: cand.equity, rate, snRate: snR, snYrs: snY, dsYrs: dsY,
        ds: annuity(cand.debt, rate, dsY), snDs: annuity(cand.sellerNote, snR, snY),
        fromCF: cand.equity, called: 0 });
      bought += 1;
    }
  }
  if (SF_CACHE.size > 200) SF_CACHE.clear();
  SF_CACHE.set(key, out);
  return out;
}

// Year-by-year war chest: what flows in, what the engine spends, what is left and how far that is
// from the next add-on's equity cheque — the data behind the add-on cash timeline.
export function warChestTimeline(M0) {
  const M = sfScope(M0);
  const H = effectiveHold(M);
  const plan = cashPlan(M);
  const rows = selfFundedAddons(M);
  const p = sfProfile(M);
  // The next cheque: the row the engine would buy next, including any EBITDA / multiple typed into it.
  const next = sfRowProfile(M, p, rows.length + 1);
  const equity = next.equity > 0 ? next.equity : p.equity;
  const need = equity + p.buffer;
  const out = plan.rows.filter((r) => r.y <= H - 1).map((r) => {
    const buys = rows.filter((a) => a.year === r.y);
    const spent = buys.reduce((a, b) => a + (b.equity || 0), 0);
    const end = Math.max(0, r.deployableEnd);
    return { y: r.y, inflow: (r.retained || 0) + (r.refiIn || 0), spent, buys: buys.length, end,
      shortfall: Math.max(0, need - end), affordable: end >= need };
  });
  const firstBuy = rows.length ? Math.min(...rows.map((r) => r.year)) : 0;
  const nextYear = (out.find((r) => r.affordable) || {}).y || 0;
  return { rows: out, need, equity, buffer: p.buffer, firstBuy, nextYear };
}

// Everything the consolidated model actually owns: committed add-ons, the back-lever synthetic and the self-funded buys.
export const consolidatedAddons = (M) => addonsOf(M);

// The self-funded buys rolled into ONE reporting line (EBITDA + package price + debt), for roll-up tables.
export function sfGroupRow(M0) {
  const M = sfScope(M0);
  const rows = selfFundedAddons(M);
  if (!rows.length) return null;
  const sum = (k) => rows.reduce((a, r) => a + (r[k] || 0), 0);
  const yrs = rows.map((r) => r.year);
  const y0 = Math.min(...yrs), y1 = Math.max(...yrs);
  const ebitda = sum("ebitda"), price = sum("price");
  return { name: `Self-funded add-ons (${rows.length})`, selfFunded: true, count: rows.length, year: y0, yearLabel: y0 === y1 ? `Yr ${y0}` : `Yr ${y0}–${y1}`,
    ebitda, price, multiple: ebitda > 0 ? price / ebitda : 0, debt: sum("debt"), sellerNote: sum("sellerNote"), rollover: sum("rollover"),
    nwc: sum("nwc"), fees: sum("fees"), equity: sum("equity"), fromCF: sum("fromCF"), called: 0,
    equityBase: Math.max(0, price - sum("debt") - sum("sellerNote") - sum("rollover")) };
}

// Every add-on table in the app: the committed roll-up rows plus the self-funded buys as one line, so
// seller notes, working capital, fees and rollover are all computed and shown for the self-funded ones too.
export function addonTableRows(M) {
  const committed = M && M.rollup ? rollupModelFor(M).rows.filter((r) => r.year > 0) : [];
  const sf = sfGroupRow(M);
  return sf ? [...committed, sf] : committed;
}

// Summary for the UI: what the engine buys, when, and what it adds.
export function selfFundedPlan(M0) {
  const M = sfScope(M0);
  const rows = selfFundedAddons(M);
  const p = sfProfile(M);
  const byYear = rows.reduce((acc, r) => { acc[r.year] = (acc[r.year] || 0) + 1; return acc; }, {});
  // Rows the user pinned to a year the war chest could not fund — they slipped to the first affordable year.
  const deferred = rows.filter((r) => r.pinnedYear && r.year > r.pinnedYear);
  return { on: !!M.sfOn, rows, profile: p, count: rows.length, byYear, deferred,
    startYear: Math.round(Number(M.sfStartYr) || 0) || retainStartYear({ ...M, _noSelfFund: true }),
    ebitdaAdded: rows.reduce((a, r) => a + r.ebitda, 0),
    equityUsed: rows.reduce((a, r) => a + r.equity, 0),
    priceTotal: rows.reduce((a, r) => a + r.price, 0),
    blocked: !!M.sfOn && (!isRetain(M) || retainUseOf(M) !== "addons") };
}


export function model5yr(M) {
  const rows = [];
  const g = M.sdeGrowth / 100;
  const esc = M.rentEsc / 100;
  const H = effectiveHold(M);
  const addons = addonsOf(M);
  const ratioRev = M.sde > 0 ? (M.ttmRevenue || 0) / M.sde : 0;
  const bl = backLever(M);
  const blY = bl && bl.active ? bl.year : Infinity;
  for (let y = 1; y <= H; y++) {
    const refi = y > blY;
    const plat_y = M.sde * Math.pow(1 + g, y - 1);
    const addon_e = addons.reduce((a, c) => a + (y > c.year ? c.ebitda * Math.pow(1 + g, y - c.year - 1) : 0), 0);
    const addon_ds = addons.reduce((a, c) => a + (y > c.year && y - c.year <= (c.dsYrs == null ? M.loanYrs : c.dsYrs) && !(refi && c.year < blY) ? c.ds : 0) + (y > c.year && y - c.year <= (c.snYrs == null ? M.snYrs : c.snYrs) ? c.snDs : 0), 0);
    const sde_y = plat_y + addon_e;
    const rent_y = M.slbOn ? M.sqft * M.rentRate * Math.pow(1 + esc, y - 1) : 0;
    const true_y = sde_y - M.salary;
    const addonRev_y = ratioRev * addons.reduce((a, c) => a + (y > c.year ? c.ebitda * Math.pow(1 + g, y - c.year - 1) : 0), 0);
    const mgmtBase_y = M.mgmtFeeBasis === "Gross Revenue" ? (M.ttmRevenue || 0) * Math.pow(1 + g, y - 1) + addonRev_y : true_y;
    const mgmt_y = Math.max(0, ((M.mgmtFeePct || 0) / 100) * mgmtBase_y);
    const cf = true_y - M.capex - rent_y - mgmt_y;
    const sn_y = y <= M.snYrs ? snAnnualDS(M) : 0;
    const senior_y = refi ? (y - blY <= bl.yrs ? bl.ds : 0) : (y <= M.loanYrs ? annualDS(M) : 0);
    const ds_y = senior_y + sn_y + addon_ds;
    const fcf = cf - ds_y;
    const dscr = ds_y > 0 ? cf / ds_y : 0;
    rows.push({ y, sde_y, plat_y, addon_e, addon_ds, rent_y, mgmt_y, salary_y: M.salary, capex_y: M.capex, cfads: cf, ds_y, sn_y, senior_y, refi, fcf, dscr });
  }
  return rows;
}

export function exitReturns(M) {
  const rows = model5yr(M);
  const plan = cashPlan(M, rows);
  const H = effectiveHold(M);
  const addons = addonsOf(M);
  const bl = plan.bl, refi = !!(bl && bl.active);
  const sdeExit = rows[H - 1] ? rows[H - 1].sde_y : 0;
  const exitEV = sdeExit * M.exitMult;
  const seniorBal = refi ? remainingBalance(bl.facility, bl.rate, bl.yrs, (H - bl.year) * 12) : remainingBalance(loanAmt(M), M.rate, M.loanYrs, H * 12);
  const snBal = H >= M.snYrs ? 0 : remainingBalance(M.snAmt, M.snRate, M.snYrs, H * 12);
  const addonBal = addons.reduce((a, c) => { const sy = c.snYrs == null ? M.snYrs : c.snYrs, sr = c.snRate == null ? M.snRate : c.snRate;
    const dy = c.dsYrs == null ? M.loanYrs : c.dsYrs;
    return a + (refi && c.year < bl.year ? 0 : remainingBalance(c.debt, c.rate, dy, (H - c.year) * 12)) + (H - c.year >= sy ? 0 : remainingBalance(c.sellerNote, sr, sy, (H - c.year) * 12)); }, 0);
  const loanBal = seniorBal + snBal + addonBal;
  const baseEquityAtExit = M.holdIndefinite ? 0 : Math.max(0, exitEV - loanBal);
  // Seller rollover: the rolled stake never funds the deal (it is its own source in S&U), but it does own
  // COMMON equity — junior to the LP preferred. So it takes no pref and no return of capital: it shares only
  // in the residual profit at exit, pro-rata to its ownership of total equity. Interim distributions stay
  // 100% with the LP/GP waterfall, and retained cash returned at exit is accumulated LP/GP cash.
  const addonRollover = addons.reduce((a, c) => a + (c.rollover || 0), 0);
  const rollEquity = rolloverAmt(M) + addonRollover;
  const sponsorEquityIn = Math.max(0, buyerCashIn(M)) + (plan.fundedAtClose || 0) + plan.rows.reduce((a, r) => a + r.called, 0);
  // Ownership: the rolled stake as a share of ALL the equity in the deal — exactly the Sources — Equity
  // table's math ($9.0m of $41.8m = 21.5%). Debt and the seller note fund the rest of the price and own
  // nothing, so they are excluded from the denominator.
  const rollPriceBase = equitySourcesTotal(M);
  const rollOwn = rollEquity > 0 && rollPriceBase > 0 ? Math.min(1, rollEquity / rollPriceBase) : 0;
  let rollExit = 0, rollResidual = 0, liqPref = 0, liqPrefDue = 0, lpEntitlement = 0, promoteAhead = 0;
  if (rollOwn > 0 && !M._noRollCut) {
    // Capital stack: senior debt → LP preferred (accrued pref, return of capital, then the liquidation
    // preference) → GP promote → rolled common. The roll is a common / profits interest by design, so it
    // sees nothing until the LP clears its liquidation preference and the promote is paid.
    const wfGross = lpWaterfall({ ...M, _noRollCut: true });
    const last = wfGross.rows[wfGross.rows.length - 1] || {};
    const prefX = Math.max(0, Number(M.liqPrefX == null ? 1 : M.liqPrefX));
    const participating = M.liqPrefType === "part";
    // Invested capital × multiple, plus any unpaid accrued pref — cumulative, so interim cash counts.
    liqPref = prefX * wfGross.lpEquity + (wfGross.accruedUnpaid || 0);
    liqPrefDue = Math.max(0, liqPref - (wfGross.interimLpTotal || 0));
    const exitPool = Math.max(0, last.cashAvail || 0);
    // GP promote ranks above the rolled common, so it comes off the pool first.
    promoteAhead = Math.min(exitPool, last.splitGp || 0);
    const netPool = Math.max(0, exitPool - promoteAhead);
    // Non-participating: the LP takes the GREATER of its liquidation preference or its pro-rata common share.
    // Participating: it takes the preference first and then still shares pro-rata in what is left.
    lpEntitlement = participating
      ? Math.min(netPool, liqPrefDue) + (1 - rollOwn) * Math.max(0, netPool - liqPrefDue)
      : Math.max(Math.min(netPool, liqPrefDue), (1 - rollOwn) * netPool);
    rollResidual = Math.max(0, netPool - lpEntitlement);
    // Hard cap: the rolled stake can never take more than its exact ownership % of the exit equity value.
    rollExit = Math.min(rollResidual, baseEquityAtExit * rollOwn);
  }
  const equityAtExit = Math.max(0, baseEquityAtExit - rollExit) + plan.leftoverReserve;
  const totalFCF = plan.rows.reduce((a, r) => a + r.distributable, 0);
  const calledTotal = plan.rows.reduce((a, r) => a + r.called, 0);
  const rolled = isRecap(M) ? recapLegacy(M).rolled : 0;
  const gpShare = M.lpEquity > 0 ? 0 : ((plan.totalDeployedCommitted || 0) + (plan.fundedAtClose || 0) + calledTotal) * (Math.min(100, Math.max(0, Number(M.gpCallPct) || 0)) / 100);
  // LPs fund the committed add-on equity at close (escrow) unless the deal is set to capital calls
  const equityInvested0 = Math.max(1, buyerCashIn(M) + (M.transExpenses || 0) + transCostsTotal(M) + (plan.fundedAtClose || 0));
  const equityInvested = equityInvested0 + calledTotal;
  const moic = (totalFCF + equityAtExit) / equityInvested;

  const flows = [-equityInvested0];
  plan.rows.forEach((r, i) => {
    flows.push(r.distributable - r.called + (i === plan.rows.length - 1 ? equityAtExit : 0));
  });
  const irr = computeIRR(flows);
  const loanPaydown = loanAmt(M) - seniorBal;
  return { sdeExit, exitEV, seniorBal, snBal, addonBal, loanBal, baseEquityAtExit, equityAtExit, totalFCF, equityInvested, equityInvested0, calledTotal, moic, irr, loanPaydown, leftoverReserve: plan.leftoverReserve,
    rollEquity, rollOwnPct: rollOwn * 100, rollExit, rollResidual, sponsorEquityIn, rollPriceBase,
    rollMoic: rollEquity > 0 ? rollExit / rollEquity : 0,
    rollIsRecap: isRecap(M),
    liqPref, liqPrefDue, liqPrefX: Math.max(0, Number(M.liqPrefX == null ? 1 : M.liqPrefX)),
    liqPrefPart: M.liqPrefType === "part", lpEntitlementAtExit: lpEntitlement, promoteAheadOfRoll: promoteAhead,
    fundedAtClose: plan.fundedAtClose || 0, equityAtCloseBase: equityInvested0 - (plan.fundedAtClose || 0),
    // what outside LPs are actually asked to wire: total equity, less the legacy equity that simply rolls over
    // in a recap, less the GP's own share of the add-on equity (same % the LP waterfall uses)
    rolledLegacy: rolled, gpShare, lpAsk: Math.max(0, equityInvested - rolled - gpShare) };
}

// ---------- cash flow policy: distribute to LPs vs retain on the platform balance sheet ----------
export const DEFAULT_CASH_POLICY = { cashPolicy: "distribute", retainPct: 100, retainThruYr: 0, retainUse: "addons" };
export const isRetain = (M) => M.cashPolicy === "retain";
// Retention % of after-debt FCF, and its mirror: the payout ratio actually distributed each year.
export const retainPctOf = (M) => Math.min(100, Math.max(0, M.retainPct == null ? 100 : Number(M.retainPct)));
export const payoutPctOf = (M) => (isRetain(M) ? 100 - retainPctOf(M) : 100);
// Where retained cash sits: "addons" = deployable war chest, "balance" = plain balance-sheet cash returned at exit.
export const retainUseOf = (M) => (M.retainUse === "balance" ? "balance" : "addons");
// What the war chest (retained cash + back-lever proceeds) does with the committed LP capital:
//  "extra"  — LP capital is still called in full as committed; the war chest buys ADDITIONAL add-ons (default)
//  "offset" — the war chest pays for scheduled add-ons first, reducing the LP capital call
// THE equity ask — the same number on the Dashboard tile, Roll-Up, S&U, Teaser and the investor room.
export const lpAskOf = (M) => exitReturns(M).lpAsk;
export const warChestUseOf = (M) => (M && M.warChestUse === "offset" ? "offset" : "extra");
// How LPs fund the committed add-on equity: one check at close (held in escrow until each add-on closes)
// or drawn down as capital calls when the add-ons close. Self-funded buys never touch either.
export const lpFundingOf = (M) => (M && M.lpFunding === "calls" ? "calls" : "close");
export const lpFundsAtClose = (M) => lpFundingOf(M) === "close";
// When retention switches on: at close, from a given year, or the first year EBITDA clears a threshold
// (i.e. once the initial commitment is deployed). Returns the first retaining year.
export const retainFromOf = (M) => (["year", "ebitda", "deployed"].includes(M.retainFrom) ? M.retainFrom : "close");
export function retainStartYear(M, rows) {
  const mode = retainFromOf(M);
  if (mode === "close") return 1;
  if (mode === "year") return Math.max(1, Math.round(Number(M.retainFromYr) || 1));
  // "deployed": the committed equity is fully deployed once the last planned add-on has closed
  if (mode === "deployed") {
    // only the committed add-ons count: a self-funded buy must never push its own trigger out (circular)
    return ((M && M.addons) || []).filter((c) => !c.selfFunded && !c.cashFunded)
      .reduce((a, c) => Math.max(a, (c.year || 0) + 1), 1);
  }
  const trigger = Math.max(0, Number(M.retainFromEbitda) || 0);
  const hit = (rows || model5yr(M)).find((r) => (r.sde_y || 0) >= trigger);
  return hit ? hit.y : Infinity;              // never reached → nothing is retained
}
// Patch produced by the payout slider: 100% payout is the plain distribute policy.
export const payoutPatch = (payout) => {
  const p = Math.min(100, Math.max(0, Number(payout) || 0));
  return p >= 100 ? { cashPolicy: "distribute", retainPct: 0 } : { cashPolicy: "retain", retainPct: 100 - p };
};

// Year-by-year: after-debt FCF is either distributed or swept into a reinvestment reserve held as cash; the reserve is returned at exit.
export function cashPlan(M, baseRows) {
  const rows = baseRows || model5yr(M);
  const H = effectiveHold(M);
  const retain = isRetain(M);
  const pct = retainPctOf(M) / 100;
  const deployRetained = retainUseOf(M) === "addons";
  const thru = M.retainThruYr > 0 ? Math.min(Number(M.retainThruYr), H - 1) : H - 1;
  const start = retainStartYear(M, rows);
  const warChest = isRecap(M) ? recapModel(M).warChestTotal : 0;
  const addons = addonsOf(M);
  const bl = backLever(M);
  const extraMode = warChestUseOf(M) === "extra";
  const atClose = lpFundsAtClose(M);
  const cashOnly = (c) => c.selfFunded || c.cashFunded;
  // LP money the committed add-ons still need after year y — the Day-0 war chest is ring-fenced for it.
  const lpNeedOf = (c) => (c.called || 0) + (c.fromCF || 0);
  const pipelineAfter = (y) => addons.filter((c) => !cashOnly(c) && c.year > y).reduce((a, c) => a + lpNeedOf(c), 0);
  // chest = LP cash raised at close for add-ons (recap war chest); reserve = retained cash + refi proceeds.
  let chest = warChest, reserve = 0, held = 0, totalRetained = 0, totalCalled = 0, totalDeployed = 0, totalSelfFunded = 0, fundedAtClose = 0, chestDeployedTotal = 0;
  const out = rows.map((r) => {
    const retaining = retain && r.y >= start && r.y <= thru;
    const retained = retaining ? Math.max(0, r.fcf) * pct : 0;
    // back-lever year: net refinance proceeds land in the add-on reserve (Variant B) and/or go straight to LPs
    const refiIn = bl && bl.active && r.y === bl.year ? bl.toReserve : 0;
    const refiLp = bl && bl.active && r.y === bl.year ? bl.toLp : 0;
    reserve += (deployRetained ? retained : 0) + refiIn;
    held += deployRetained ? 0 : retained;
    totalRetained += retained;
    const closing = addons.filter((c) => c.year === r.y);
    // 1. Committed add-ons: the war chest the LPs funded at close pays first (it was raised for exactly this),
    //    then — in offset mode — retained cash / refi proceeds; whatever is left is LP money (escrow or a call).
    const commNeed = closing.filter((c) => !cashOnly(c)).reduce((a, c) => a + lpNeedOf(c), 0);
    const chestDeployed = Math.min(chest, commNeed); chest -= chestDeployed;
    const commWant = extraMode ? 0 : Math.max(0, commNeed - chestDeployed);
    const commDeployed = Math.min(reserve, commWant); reserve -= commDeployed;
    const lpNeed = Math.max(0, commNeed - chestDeployed - commDeployed);
    // 2. Cash-only add-ons (self-funded engine + back-lever recycling): retained cash / refi first, then any war
    //    chest not ring-fenced for the remaining committed pipeline. Never an LP call — no cash, no purchase.
    const cashWant = closing.filter(cashOnly).reduce((a, c) => a + (c.fromCF || 0), 0);
    const fromReserve = Math.min(reserve, cashWant); reserve -= fromReserve;
    const chestFree = Math.max(0, chest - pipelineAfter(r.y));
    const fromChest = Math.min(chestFree, cashWant - fromReserve); chest -= fromChest;
    const cashDeployed = fromReserve + fromChest;
    const deployed = chestDeployed + commDeployed + cashDeployed;
    const called = atClose ? 0 : lpNeed;
    const escrowDraw = atClose ? lpNeed : 0;
    totalCalled += called; totalDeployed += deployed; totalSelfFunded += cashDeployed; fundedAtClose += escrowDraw; chestDeployedTotal += chestDeployed + fromChest;
    return { y: r.y, fcf: r.fcf, retained, refiIn, refiLp, distributable: r.fcf - retained + refiLp, deployed, called, escrowDraw, lpNeed, chestDeployed, commNeed,
      reserveEnd: reserve + chest + held, deployableEnd: reserve + Math.max(0, chest - pipelineAfter(r.y)), heldEnd: held, chestEnd: chest };
  });
  reserve += chest;   // any war chest never spent sits as cash and comes back at exit with the rest of the reserve
  const cashEnd = reserve + held;
  return { rows: out, retain, pct, thru, start, retainFrom: retainFromOf(M), warChest, bl, refiToReserve: bl && bl.active ? bl.toReserve : 0, refiToLp: bl && bl.active ? bl.toLp : 0,
    reserveAtExit: cashEnd, leftoverReserve: M.holdIndefinite ? 0 : cashEnd, heldCash: held, totalRetained, totalCalled,
    totalDeployed, totalSelfFunded, warChestUse: warChestUseOf(M), lpFunding: lpFundingOf(M), fundedAtClose, lpAddonEquity: totalCalled + fundedAtClose,
    chestDeployed: chestDeployedTotal, totalDeployedCommitted: Math.max(0, totalDeployed - totalSelfFunded) };
}

// Year-by-year distribution schedule: what the business earns, what stays on the balance sheet
// and what actually reaches LPs and the GP after the waterfall.
export function distributionSchedule(M) {
  const rows = model5yr(M);
  const plan = cashPlan(M, rows);
  const wf = lpWaterfall(M);
  const H = effectiveHold(M);
  const base = wf.lpEquity > 0 ? wf.lpEquity : 1;
  const out = plan.rows.map((r, i) => {
    const w = wf.rows[i] || {};
    const exitCash = r.y === H ? Math.max(0, (w.cashAvail || 0) - Math.max(0, r.distributable)) : 0;
    return { y: r.y, fcf: r.fcf, retained: r.retained, refiIn: r.refiIn, refiLp: r.refiLp,
      fromOps: r.fcf - r.retained, distributable: r.distributable, cashOnBs: r.reserveEnd, deployed: r.deployed, called: r.called,
      exitCash, cashAvail: w.cashAvail || 0, pref: w.prefPaid || 0, capRet: w.capRet || 0,
      lp: w.lpYear || 0, gp: w.gpYear || 0, lpNet: (w.lpYear || 0) - (w.called || 0),
      lpYield: (((w.lpYear || 0) - (w.called || 0)) / base) * 100 };
  });
  const tot = (k) => out.reduce((a, r) => a + (r[k] || 0), 0);
  const interim = out.slice(0, -1);
  return { rows: out, payoutPct: payoutPctOf(M), retain: plan.retain, retainUse: retainUseOf(M), thru: plan.thru,
    start: plan.start, retainFrom: plan.retainFrom, hold: H,
    lpEquity: wf.lpEquity, lpEquity0: wf.lpEquity0, cashAtExit: plan.reserveAtExit, heldCash: plan.heldCash,
    totals: { fcf: tot("fcf"), retained: tot("retained"), fromOps: tot("fromOps"),
      refiIn: tot("refiIn"), refiLp: tot("refiLp"), distributable: tot("distributable"), lp: tot("lp"), gp: tot("gp"),
      called: tot("called"), lpNet: tot("lpNet"), exitCash: tot("exitCash") },
    interimLp: interim.reduce((a, r) => a + r.lp, 0),
    avgLpYield: interim.length ? interim.reduce((a, r) => a + r.lpYield, 0) / interim.length : 0 };
}

// Compare LP outcomes under both policies.
export function policyCompare(M) {
  const mk = (p) => {
    const MM = isRollupScope(M) ? rollupScopedModel({ ...M, addons: undefined, cashPolicy: p }) : { ...M, cashPolicy: p };
    const wf = lpWaterfall(MM); const ex = exitReturns(MM); const plan = cashPlan(MM); return { wf, ex, plan };
  };
  return { distribute: mk("distribute"), retain: mk("retain") };
}

// Roll-up add-on funding: how much of each add-on's equity check is covered by retained cash flow of the companies already owned
// (plus, in the back-lever year, the net refinance proceeds earmarked for the pipeline).
export function addonFunding(M, forcePct, opts = {}) {
  const R = rollupCfgFor(M);
  const out = rollupModelFor(M);
  const H = effectiveHold(M);
  const pct = forcePct != null ? forcePct : isRetain(M) && retainUseOf(M) === "addons" ? retainPctOf(M) / 100 : 0;
  const thru = M.retainThruYr > 0 ? Number(M.retainThruYr) : Infinity;
  const startYr = retainStartYear(M, model5yr(M));
  const rate = (R.debtRate || 0) / 100;
  const cf = (r) => r.ebitda - r.debt * rate - (r.debt * ((R.debtPaydownPct || 0) / 100)) / out.holdYears - r.sellerNote * rate;
  const rows = out.rows.map((r, i) => ({ ...r, idx: i, cf: cf(r) })).sort((a, b) => a.year - b.year || a.idx - b.idx);
  const maxYear = rows.reduce((a, r) => Math.max(a, r.year), 0);
  const warChest = opts.noWarChest || !isRecap(M) ? 0 : recapModel(M).warChestTotal;
  const bl = backLever({ ...M, addons: out.rows.filter((r) => r.year > 0 && r.year < H).map((r) => addonBase(M, R, r)) });
  let pool = warChest;
  const funded = new Map();
  for (let t = 1; t <= maxYear; t++) {
    const gen = rows.filter((r) => r.year < t).reduce((a, r) => a + Math.max(0, r.cf), 0);
    const retained = t >= startYr && t <= thru ? gen * pct : 0;
    pool += retained;
    if (bl && bl.active && t === bl.year) pool += bl.toReserve;
    rows.filter((r) => r.year === t).forEach((r) => {
      const use = Math.min(pool, r.equity); pool -= use;
      funded.set(r.idx, { generated: gen, retained, availBefore: pool + use, fromCF: use, called: r.equity - use });
    });
  }
  const res = rows.map((r) => ({ ...r, ...(funded.get(r.idx) || { generated: 0, retained: 0, availBefore: 0, fromCF: 0, called: r.equity }) }));
  const totEquity = res.reduce((a, r) => a + r.equity, 0);
  const totFromCF = res.reduce((a, r) => a + r.fromCF, 0);
  return { rows: res, totEquity, totFromCF, totCalled: totEquity - totFromCF, pctSelfFunded: totEquity > 0 ? (totFromCF / totEquity) * 100 : 0, pct, leftoverPool: pool, warChest,
    refiToPool: bl && bl.active ? bl.toReserve : 0, bl, holdYears: out.holdYears };
}

// Cash we must raise up front to fund the add-ons: the total sponsor equity across every add-on
// (the platform's own Year-0 check is funded in the recap/purchase Sources, not the war chest).
export function addonEquityNeed(M) {
  return rollupModelFor(M).rows.filter((r) => r.year > 0).reduce((a, r) => a + r.equity, 0);
}

export function computeIRR(flows) {  let lo = -0.9, hi = 2.0;
  const npv = (r) => flows.reduce((a, cf, i) => a + cf / Math.pow(1 + r, i), 0);
  let fLo = npv(lo), fHi = npv(hi);
  if (fLo * fHi > 0) return NaN;
  for (let i = 0; i < 80; i++) {
    const mid = (lo + hi) / 2;
    const fMid = npv(mid);
    if (Math.abs(fMid) < 1) return mid * 100;
    if (fLo * fMid < 0) { hi = mid; fHi = fMid; } else { lo = mid; fLo = fMid; }
  }
  return ((lo + hi) / 2) * 100;
}

// ---------- LP / GP waterfall ----------
export function lpEquityBase(M) {
  return M.lpEquity && M.lpEquity > 0 ? M.lpEquity : exitReturns(M).equityInvested;
}

// Single-deal European-style waterfall: pref -> return of capital -> promote split (with MOIC step-up).
export function lpWaterfall(M, lpAmount) {
  const prefRate = (M.prefReturn || 0) / 100;
  const lpShare1 = (M.lpSplit == null ? 80 : M.lpSplit) / 100;
  const gpShare1 = 1 - lpShare1;
  const stepMoic = M.moicStepUp || 0;
  const gpShare2 = (M.gpPromoteStep == null ? gpShare1 * 100 : M.gpPromoteStep) / 100;
  const stepMoic2 = M.moicStepUp2 || 0;
  const gpShare3 = (M.gpPromoteStep2 == null ? gpShare2 * 100 : M.gpPromoteStep2) / 100;
  // Ordered promote tiers by cumulative LP MOIC hurdle.
  const tiers = [];
  if (stepMoic > 0) {
    tiers.push({ untilMoic: stepMoic, lpShare: lpShare1, gpShare: gpShare1 });
    if (stepMoic2 > stepMoic) {
      tiers.push({ untilMoic: stepMoic2, lpShare: 1 - gpShare2, gpShare: gpShare2 });
      tiers.push({ untilMoic: Infinity, lpShare: 1 - gpShare3, gpShare: gpShare3 });
    } else {
      tiers.push({ untilMoic: Infinity, lpShare: 1 - gpShare2, gpShare: gpShare2 });
    }
  } else {
    tiers.push({ untilMoic: Infinity, lpShare: lpShare1, gpShare: gpShare1 });
  }

  const rows = model5yr(M);
  const plan = cashPlan(M, rows);
  const ex = exitReturns(M);
  const hold = effectiveHold(M);

  // The GP's share is a % of the FULL additional equity the add-ons need — the war chest never nets it
  // down. The GP funds its slice of the Day-0 reserve at close and its slice of every later call, so
  // only the LP slice accrues pref and counts towards LP invested capital.
  const gpCall = Math.min(100, Math.max(0, Number(M.gpCallPct) || 0)) / 100;
  const deployedTotal = (plan.totalDeployedCommitted != null ? plan.totalDeployedCommitted : (plan.totalDeployed || 0)) + (plan.fundedAtClose || 0);
  const selfFundedTotal = plan.totalSelfFunded || 0;
  const fixedLp = !!(lpAmount || (M.lpEquity && M.lpEquity > 0));
  const gpDay0 = fixedLp ? 0 : deployedTotal * gpCall;

  // Day-0 LP equity; add-on capital calls (consolidated view) are layered in the year they close.
  const lpEquity0 = Math.max(1, (lpAmount || (M.lpEquity && M.lpEquity > 0 ? M.lpEquity : ex.equityInvested0)) - gpDay0);
  let unreturned = lpEquity0;
  let invested = lpEquity0;
  let accruedPref = 0;
  let lpCum = 0;
  let carry = 0;
  const out = [];
  let prefTotal = 0, capTotal = 0, lpTotal = 0, gpTotal = 0;

  let grossTotal = deployedTotal, newMoneyTotal = 0, gpCalledSum = 0;
  for (let y = 1; y <= hold; y++) {
    const row = plan.rows[y - 1];
    const calledTot = row ? row.called : 0;
    const gpCalled = calledTot * gpCall;
    const called = calledTot - gpCalled;
    grossTotal += calledTot; newMoneyTotal += calledTot; gpCalledSum += gpCalled;
    unreturned += called; invested += called;
    accruedPref += unreturned * prefRate;
    // A negative operating year is carried forward against later cash, never simply ignored.
    const raw = (plan.rows[y - 1] ? plan.rows[y - 1].distributable : 0) + carry;
    const fcf = Math.max(0, raw);
    carry = Math.min(0, raw);
    const exitCash = y === hold ? Math.max(0, ex.equityAtExit) : 0;
    let cash = fcf + exitCash;
    const cashAvail = cash;

    let lpYear = 0, gpYear = 0, prefPaid = 0, capRet = 0, splitLp = 0, splitGp = 0;

    const payPref = Math.min(cash, accruedPref);
    accruedPref -= payPref; cash -= payPref; prefPaid = payPref; lpYear += payPref; prefTotal += payPref;

    const payCap = Math.min(cash, unreturned);
    unreturned -= payCap; cash -= payCap; capRet = payCap; lpYear += payCap; capTotal += payCap;

    if (cash > 0) {
      let remaining = cash, lpCumT = lpCum + lpYear;
      for (const t of tiers) {
        if (remaining <= 0) break;
        const lpRoom = t.untilMoic === Infinity ? Infinity : Math.max(0, t.untilMoic * invested - lpCumT);
        const useCash = lpRoom === Infinity ? remaining : (t.lpShare > 0 ? Math.min(remaining, lpRoom / t.lpShare) : remaining);
        const lpP = useCash * t.lpShare, gpP = useCash * t.gpShare;
        splitLp += lpP; splitGp += gpP; lpCumT += lpP; remaining -= useCash;
      }
      lpYear += splitLp; gpYear += splitGp;
    }

    lpCum += lpYear; lpTotal += lpYear; gpTotal += gpYear;
    out.push({ y, cashAvail, called, calledTot, gpCalled, prefPaid, capRet, splitLp, splitGp, lpYear, gpYear, unreturned, lpCumMoic: lpCum / invested });
  }

  const lpEquity = invested;
  const flows = [-lpEquity0, ...out.map((r) => r.lpYear - r.called)];
  const lpIrr = computeIRR(flows);
  const lpMoic = lpTotal / lpEquity;
  const calledTotal = lpEquity - lpEquity0;
  // Additional equity = everything the add-ons need beyond the platform close (day-1 reserve deployed
  // + every later call). It is independent of the GP's share, which is a straight % of that total.
  return { lpEquity, lpEquity0, calledTotal, gpCalledTotal: gpDay0 + gpCalledSum, gpDay0, deployedTotal,
    newMoneyTotal, addlEquity: grossTotal, selfFundedTotal, gpCallPct: gpCall * 100,
    accruedUnpaid: Math.max(0, accruedPref),
    interimLpTotal: out.slice(0, -1).reduce((a, r) => a + r.lpYear, 0),
    rows: out, prefTotal, capTotal, lpTotal, gpTotal, lpMoic, lpIrr };
}

// Per-investor slice + best single-lever adjustment.
// `amount` is the investor's share of the Day-0 raise; add-on capital calls are funded pro-rata,
// so yearly cash is net of calls and MOIC is on total dollars invested (Day-0 + calls).
export function investorReturn(M, amount, poolEquity = 0) {
  const wf = lpWaterfall(M);
  const pool = poolEquity > 0 ? poolEquity : wf.lpEquity0;
  const f = pool > 0 && amount > 0 ? amount / pool : 0;
  const rows = wf.rows.map((r) => ({ y: r.y, gross: r.lpYear * f, called: r.called * f, cash: (r.lpYear - r.called) * f }));
  const total = wf.lpTotal * f;
  const called = wf.calledTotal * f;
  const invested = amount + called;
  const flows = [-amount, ...rows.map((r) => r.cash)];
  const moic = invested > 0 ? total / invested : 0;
  return { amount, called, invested, rows, total, profit: total - invested, moic, irr: amount > 0 && total > 0 ? computeIRR(flows) : NaN, share: f * 100, pool };
}

export function lpAdjustments(M) {
  const base = lpWaterfall(M).lpIrr;
  const scenarios = [
    { label: "Extend hold +1 year", patch: { hold: Math.min(10, (M.hold || 5) + 1) } },
    { label: "Negotiate exit multiple +0.5×", patch: { exitMult: (M.exitMult || 3) + 0.5 } },
    { label: "Negotiate entry price −5%", patch: { askPrice: Math.round((M.askPrice || 0) * 0.95) } },
    { label: "Drive SDE growth +1%/yr", patch: { sdeGrowth: (M.sdeGrowth || 0) + 1 } },
    { label: "Trim GP promote by 5 pts", patch: { lpSplit: Math.min(100, (M.lpSplit == null ? 80 : M.lpSplit) + 5) } },
  ];
  const list = scenarios.map((s) => {
    const irr = lpWaterfall({ ...M, ...s.patch }).lpIrr;
    const delta = (isNaN(irr) ? -999 : irr) - (isNaN(base) ? -999 : base);
    return { label: s.label, patch: s.patch, irr, delta };
  }).sort((a, b) => b.delta - a.delta);
  return { base, list };
}

export function bestLpAdjustment(M) {
  const { base, list } = lpAdjustments(M);
  return { base, ...list[0] };
}

// ---------- roll-up / buy-and-build model ----------
export const DEFAULT_ROLLUP = {
  exitMultiple: 8,
  holdYears: 5,
  debtPct: 55,
  sellerNotePct: 10,
  rolloverPct: 10,
  debtRate: 10,
  debtPaydownPct: 25,
  snRate: null,
  snYrs: null,
  companies: [
    { name: "Platform Co", ebitda: 2000000, multiple: 5.5, year: 0 },
    { name: "Add-on 1", ebitda: 750000, multiple: 4.0, year: 1 },
    { name: "Add-on 2", ebitda: 600000, multiple: 3.75, year: 2 },
  ],
  backLever: {
    committedEquity: 25000000, entryMultiple: 4.0, backLeverYear: 2, refiMultiple: 4.0,
    exitYear: 5, exitMultiple: 7.0, exitEbitda: 20000000, prefReturn: 8, promote: 20,
    lenderMult: 2.5,
    levAEbitda: 2000000, levAMult: 6.0,
    levBEbitda: 500000, levBMult: 3.0,
  },
};

// Illustrative "all-equity in, back-lever later" roll-up economics (values in $M).
export const DEFAULT_BACKLEVER = DEFAULT_ROLLUP.backLever;

export function backLeverModel(B0) {
  const B = { ...DEFAULT_BACKLEVER, ...(B0 || {}) };
  const committedEquity = Number(B.committedEquity) || 0;
  const entryMultiple = Number(B.entryMultiple) || 1;
  const refiMultiple = Number(B.refiMultiple) || 0;
  const exitMultiple = Number(B.exitMultiple) || 0;
  const exitYear = Math.max(1, Number(B.exitYear) || 5);
  const backLeverYear = Math.max(1, Number(B.backLeverYear) || 2);
  const exitEbitda = Number(B.exitEbitda) || 0;
  const pref = (Number(B.prefReturn) || 0) / 100;
  const promotePct = (Number(B.promote) || 0) / 100;

  // Year 0 — entry (all equity)
  const capitalDeployed = committedEquity;
  const ebitdaAcquired = entryMultiple ? capitalDeployed / entryMultiple : 0;
  // Back-lever year — refinance the platform, redeploy proceeds into M&A
  const platformEbitdaPre = ebitdaAcquired;
  const refiProceeds = platformEbitdaPre * refiMultiple;
  const mAndA = refiProceeds;
  const newEbitda = entryMultiple ? mAndA / entryMultiple : 0;
  const platformEbitdaPost = platformEbitdaPre + newEbitda;
  // Exit
  const platformEbitdaExit = exitEbitda;
  const enterpriseValue = platformEbitdaExit * exitMultiple;
  const netDebt = refiProceeds;
  const equityValue = Math.max(0, enterpriseValue - netDebt);
  // Distribution — promote over a preferred return
  const prefAmount = committedEquity * (Math.pow(1 + pref, exitYear) - 1);
  const profitAbovePref = Math.max(0, equityValue - committedEquity - prefAmount);
  const operatorPromote = profitAbovePref * promotePct;
  const toInvestors = Math.max(0, equityValue - operatorPromote);
  const moic = committedEquity ? toInvestors / committedEquity : 0;
  const irr = committedEquity && toInvestors > 0 ? Math.pow(toInvestors / committedEquity, 1 / exitYear) - 1 : 0;

  return {
    committedEquity, entryMultiple, refiMultiple, exitMultiple, exitYear, backLeverYear,
    capitalDeployed, ebitdaAcquired, platformEbitdaPre, refiProceeds, mAndA, newEbitda, platformEbitdaPost,
    platformEbitdaExit, enterpriseValue, netDebt, equityValue, prefAmount, operatorPromote, toInvestors, moic, irr,
    prefPct: Number(B.prefReturn) || 0, promotePct: Number(B.promote) || 0,
  };
}

export const hasRollup = (M) => {
  const R = { ...DEFAULT_ROLLUP, ...((M && M.rollup) || {}) };
  return (R.companies || []).length > 0;
};

export function rollupModel(R0, extrasFor) {
  const R = { ...DEFAULT_ROLLUP, ...(R0 || {}) };
  const companies = R.companies && R.companies.length ? R.companies : DEFAULT_ROLLUP.companies;
  const dPct = (R.debtPct || 0) / 100;
  const snPct = (R.sellerNotePct || 0) / 100;
  const roPct = (R.rolloverPct || 0) / 100;
  const rows = companies.map((c) => {
    const ebitda = Number(c.ebitda) || 0;
    const multiple = Number(c.multiple) || 0;
    const price = ebitda * multiple;
    const debt = price * dPct;
    const sellerNote = price * snPct;
    const year = Number(c.year) || 0;
    // The platform's own roll is set on Assumptions (or is the legacy equity not cashed out in a recap),
    // not the roll-up add-on rollover %, so the platform row ties to S&U and the consolidated total.
    const rollover = price * roPct;
    const equityBase = Math.max(0, price - debt - sellerNote - rollover);
    const x = year > 0 && typeof extrasFor === "function" ? extrasFor({ ebitda, multiple, year, price, debt, sellerNote, rollover, equityBase }) || {} : {};
    const nwc = Math.max(0, Number(x.nwc) || 0), fees = Math.max(0, Number(x.fees) || 0);
    return { name: c.name || "Company", ebitda, multiple, year, price, debt, sellerNote, rollover, equityBase, nwc, fees, equity: equityBase + nwc + fees };
  });
  const sum = (k) => rows.reduce((a, r) => a + (r[k] || 0), 0);
  const totEbitda = sum("ebitda");
  const totPrice = sum("price");
  const totDebt = sum("debt");
  const totSN = sum("sellerNote");
  const totRollover = sum("rollover");
  const totSponsorEquity = sum("equity");
  const totNwc = sum("nwc");
  const totFees = sum("fees");
  const totEquity = totSponsorEquity + totRollover;
  const blendedEntry = totEbitda > 0 ? totPrice / totEbitda : 0;
  const exitMultiple = Number(R.exitMultiple) || 0;
  const holdYears = Math.max(1, Number(R.holdYears) || 5);
  const exitEV = totEbitda * exitMultiple;
  const debtAtExit = totDebt * (1 - (R.debtPaydownPct || 0) / 100) + totSN;
  const exitEquityValue = Math.max(0, exitEV - debtAtExit);
  const sponsorShare = totEquity > 0 ? totSponsorEquity / totEquity : 1;
  const sponsorProceeds = exitEquityValue * sponsorShare;
  const moic = totSponsorEquity > 0 ? sponsorProceeds / totSponsorEquity : 0;
  const valueCreated = exitEV - totPrice;
  const multipleArb = exitMultiple - blendedEntry;
  // IRR: time each acquisition's sponsor equity outflow at its year; single exit at holdYears.
  const flows = Array(holdYears + 1).fill(0);
  rows.forEach((r) => { const y = Math.min(Math.max(0, r.year), holdYears); flows[y] -= r.equity; });
  flows[holdYears] += sponsorProceeds;
  const irr = computeIRR(flows);
  return {
    rows, totEbitda, totPrice, totDebt, totSN, totRollover, totSponsorEquity, totNwc, totFees, totEquity,
    blendedEntry, exitMultiple, holdYears, exitEV, debtAtExit, exitEquityValue, sponsorShare,
    sponsorProceeds, moic, irr, valueCreated, multipleArb,
  };
}

// ---------- view scope: this deal only vs. consolidated roll-up (platform + add-ons) vs. one add-on standalone ----------
export const SCOPE_PLATFORM = "platform", SCOPE_ROLLUP = "rollup";
const rollupAddons = (M) => {
  const R = { ...DEFAULT_ROLLUP, ...((M && M.rollup) || {}) };
  return (R.companies || []).map((c, idx) => ({ ...c, idx })).filter((c) => Number(c.year) > 0);
};
export function scopeOptions(M) {
  if (!M || !M.rollup) return null;
  const addons = rollupAddons(M);
  if (!addons.length) return null;
  const name = isRecap(M) ? recapCfg(M).parentName || M.companyName || M.dealName : M.companyName || M.dealName;
  return [
    { id: SCOPE_PLATFORM, label: `This deal only — ${name}`, short: "Platform only" },
    { id: SCOPE_ROLLUP, label: `Platform + ${addons.length} add-on${addons.length > 1 ? "s" : ""} (Roll-Up consolidated)`, short: "Roll-Up" },
    ...addons.map((c) => ({ id: `addon:${c.idx}`, label: `Add-on · ${c.name || "Company"} (standalone)`, short: c.name || "Add-on" })),
  ];
}
export const scopeLabel = (M, scope) => (((scopeOptions(M) || []).find((o) => o.id === scope) || {}).short || "");
export const isRollupScope = (V) => !!(V && V.addons && V.addons.length);
// Which deal view to open on when nothing was ever picked: a recap that owns add-ons is a
// consolidated platform story, so don't strand legacy deals (and their rooms) on the standalone view.
export const defaultScope = (M) => {
  if (M && M.viewScope) return M.viewScope;
  const opts = scopeOptions(M) || [];
  return isRecap(M || {}) && opts.some((o) => o.id === SCOPE_ROLLUP) ? SCOPE_ROLLUP : SCOPE_PLATFORM;
};


// ---------- pro-forma consolidated basis (roll-up views) ----------
// Add-on revenue is not modelled directly, so consolidated revenue is grossed up at the platform's
// revenue-per-dollar-of-earnings. Labelled "pro-forma, at full deployment" everywhere it is shown.
export function proForma(V) {
  const rev = Number((V && V.ttmRevenue) || 0), sde0 = Number((V && V.sde) || 0);
  if (!isRollupScope(V)) return { pf: false, sde: sde0, revenue: rev, addonEbitda: 0, count: 0 };
  const out = rollupModelFor(V);
  const sf = sfGroupRow(V);
  const sde = out.totEbitda + (sf ? sf.ebitda : 0);
  const perDollar = sde0 > 0 ? rev / sde0 : 0;
  return { pf: true, sde, revenue: Math.round(sde * perDollar), perDollar, sf,
    addonEbitda: Math.max(0, sde - sde0), count: out.rows.filter((r) => Number(r.year) > 0).length + (sf ? sf.count : 0) };
}

// First year every scheduled add-on has been owned for a full year — the year investors read coverage on.
export function deployedYear(V) {
  const H = effectiveHold(V);
  const yrs = ((V && V.addons) || []).filter((a) => Number(a.year) > 0).map((a) => Number(a.year));
  return yrs.length ? Math.min(H, Math.max(...yrs) + 1) : 1;
}

// Headline debt-service coverage: year 1 for a single deal, the fully-deployed year for a roll-up.
export function coverage(V) {
  const year = deployedYear(V);
  if (year <= 1) return { year: 1, dscr: dscrYr1(V), pf: false };
  const row = model5yr(V).find((r) => r.y === year);
  return { year, dscr: row ? row.dscr : dscrYr1(V), pf: true };
}

// Entry multiple as the app states it for the active view: this deal's NAC / SDE on the platform view,
// and the consolidated total price ÷ total EBITDA (the same "Blended Entry Multiple" shown on
// Assumptions → Roll-Up and the Roll-Up page) whenever the view is the consolidated roll-up.
export const entryMultiple = (V) => (isRollupScope(V) ? rollupModelFor(V).blendedEntry : V && V.sde > 0 ? nac(V) / V.sde : 0);

// The Year-0 row of the roll-up as this deal's model would state it (name / EBITDA / entry multiple).
export const platformRowFromModel = (M) => ({
  name: isRecap(M) ? recapCfg(M).parentName || M.companyName || M.dealName : M.companyName || M.dealName,
  ebitda: Math.round(M.sde || 0), multiple: M.sde > 0 ? Math.round((M.askPrice / M.sde) * 100) / 100 : 0, year: 0,
});
// Roll-up config for the UI: when no roll-up is attached yet, seed it from THIS deal
// (never the sample companies) so the Roll-Up tab and Assumptions → Roll-Up always agree.
// Roll-up model for THIS deal: deal-seeded config plus the deal's working-capital method on each add-on.
export const rollupModelFor = (M) => rollupModel(rollupCfgFor(M), (row) => ({ nwc: addonWC(M, row.ebitda), fees: addonFeesFor(M, row) }));

// Headline roll-up outputs. Once the roll-up is attached to the deal, every figure comes from the
// SAME consolidated model the Assumptions rail, Returns page and the investor room use — the builder's
// standalone estimator is only used for a draft that isn't attached yet.
export function rollupHeadline(M) {
  const out = rollupModelFor(M);
  const R = rollupCfgFor(M);
  const attached = !!(M && M.rollup) && (R.companies || []).some((c) => Number(c.year) > 0);
  if (!attached) return { ...out, live: false };
  const V = scopedModel(M, SCOPE_ROLLUP);
  const A = consolidatedAddons(V);       // committed + back-lever + self-funded: the EBITDA we actually own
  const sum = (k) => A.reduce((a, c) => a + (c[k] || 0), 0);
  const ex = exitReturns(V);
  const plan = cashPlan(V);
  const sf = sfGroupRow(V);
  const totEbitda = (V.sde || 0) + sum("ebitda");
  const totPrice = (V.askPrice || 0) + sum("price");
  const blendedEntry = totEbitda > 0 ? totPrice / totEbitda : 0;
  const exitMultiple = Number(V.exitMult) || 0;
  const exitEquityValue = Math.max(0, ex.exitEV - ex.loanBal);
  return {
    ...out, live: true, V, ex, plan, sf,
    totEbitda, totPrice, blendedEntry, exitMultiple,
    multipleArb: exitMultiple - blendedEntry,
    totDebt: loanAmt(V) + sum("debt"),
    totSN: (V.snAmt || 0) + sum("sellerNote"),
    totRollover: rolloverAmt(V) + sum("rollover"),
    totNwc: sum("nwc"), totFees: sum("fees"),
    totSponsorEquity: ex.equityInvested,   // total sponsor equity (incl. rolled legacy equity in a recap)
    lpAsk: ex.lpAsk, rolledLegacy: ex.rolledLegacy,   // cash the LPs wire — self-funded buys are paid from retained cash, never raised
    selfFundedEquity: sf ? sf.equity : 0,
    exitEV: ex.exitEV,
    debtAtExit: ex.loanBal,
    exitEquityValue,
    sponsorShare: exitEquityValue > 0 ? ex.equityAtExit / exitEquityValue : 1,
    sponsorProceeds: ex.equityAtExit,
    moic: ex.moic,
    irr: ex.irr,
    holdYears: effectiveHold(V),
    valueCreated: Math.max(0, (exitMultiple - blendedEntry) * totEbitda),
  };
}


export const rollupCfgFor = (M) => {
  const R = { ...DEFAULT_ROLLUP, ...((M && M.rollup) || {}) };
  const cos = ((M && M.rollup) || {}).companies;
  return Array.isArray(cos) && cos.length ? R : { ...R, companies: [platformRowFromModel(M || {})] };
};

// Average add-on vs. consolidated platform — drives the absolute/relative leverage table off THIS deal.
export const levCompare = (M) => {
  const out = rollupModelFor(M);
  const rows = (out.rows || []).filter((r) => r.year > 0);
  const avgEb = rows.length ? rows.reduce((a, r) => a + r.ebitda, 0) / rows.length : 0;
  const avgPr = rows.length ? rows.reduce((a, r) => a + r.price, 0) / rows.length : 0;
  return {
    addon: { label: `Average add-on${rows.length ? ` (of ${rows.length})` : ""}`, count: rows.length, ebitda: avgEb, mult: avgEb ? avgPr / avgEb : 0 },
    all: { label: "Platform + all add-ons", ebitda: out.totEbitda, mult: out.blendedEntry },
  };
};


export function platformRowSynced(M) {
  const p = (((M && M.rollup) || {}).companies || []).find((c) => Number(c.year) === 0);
  const want = platformRowFromModel(M);
  return !!p && Math.round(p.ebitda || 0) === want.ebitda && Math.abs((p.multiple || 0) - want.multiple) < 0.005;
}
const rollupWithPlatformRow = (M) => {
  const R = { ...DEFAULT_ROLLUP, ...(M.rollup || {}) };
  const cos = [...(R.companies || [])];
  const i = cos.findIndex((c) => Number(c.year) === 0);
  const row = platformRowFromModel(M);
  if (i >= 0) cos[i] = { ...cos[i], ...row }; else cos.unshift(row);
  return { ...R, companies: cos };
};

// One add-on as the consolidated model sees it (terms from the roll-up config, debt service as level annuities).
const addonBase = (M, R, r) => {
  const rate = Number(R.debtRate) || M.rate, snR = addonSnRate(M), snY = addonSnYrs(M);
  return { name: r.name, year: r.year, ebitda: r.ebitda, multiple: r.multiple, price: r.price, debt: r.debt, sellerNote: r.sellerNote, rollover: r.rollover,
    nwc: r.nwc || 0, fees: r.fees || 0, equity: r.equity, rate, snRate: snR, snYrs: snY, dsYrs: addonDebtYrs(M), ds: annuity(r.debt, rate, addonDebtYrs(M)), snDs: annuity(r.sellerNote, snR, snY) };
};

export function rollupScopedModel(M) {
  const R = rollupWithPlatformRow(M);
  const H = effectiveHold(M);
  const addons = addonFunding({ ...M, rollup: R }).rows.filter((r) => r.year > 0 && r.year < H).map((r) => ({ ...addonBase(M, R, r), fromCF: r.fromCF, called: r.called }));
  const V = { ...M, _scope: SCOPE_ROLLUP, addons, exitMult: Number(R.exitMultiple) || M.exitMult };
  const syn = blSyntheticAddon(V);
  return syn ? { ...V, addons: [...addons, syn] } : V;
}

// EBITDA as the Dashboard shows it for the deal's saved view: run-rate (platform + every add-on once
// closed) on the consolidated Roll-Up view, stated EBITDA on the platform / standalone view.
export function runRateEbitda(M0) {
  const M = coerceModel(M0 || {});
  const V = scopedModel(M, defaultScope(M));
  if (!isRollupScope(V)) return Number(V.sde) || 0;
  return (Number(V.sde) || 0) + consolidatedAddons(V).reduce((a, c) => a + (Number(c.ebitda) || 0), 0);
}
export const isRunRateView = (M0) => isRollupScope(scopedModel(coerceModel(M0 || {}), defaultScope(coerceModel(M0 || {}))));


// Variant B: refinance proceeds the scheduled pipeline does not need buy more EBITDA — modelled as one extra
// all-cash add-on closing in the back-lever year at the blended add-on entry multiple (no new acquisition debt,
// the leverage already sits in the refinance facility). Its EBITDA phases in from the following year.
export const BL_ADDON_NAME = "Back-lever add-ons (refi proceeds)";
export function blSyntheticAddon(V) {
  if (!isBackLever(V) || V.blUse === "distribute") return null;
  const bl = backLever(V);
  if (!bl || !bl.active || bl.toLp <= 0.5) return null;
  const rows = rollupModelFor(V).rows.filter((x) => x.year > 0);
  const mult = rows.length ? rows.reduce((a, x) => a + x.price, 0) / Math.max(1, rows.reduce((a, x) => a + x.ebitda, 0)) : V.sde > 0 ? V.askPrice / V.sde : 4;
  const price = bl.toLp;
  return { name: BL_ADDON_NAME, synthetic: true, cashFunded: true, year: bl.year, ebitda: mult > 0 ? price / mult : 0, multiple: mult, price, debt: 0, sellerNote: 0, rollover: 0,
    nwc: 0, fees: 0, equity: price, fromCF: price, called: 0, rate: 0, snRate: 0, snYrs: 0, dsYrs: 0, ds: 0, snDs: 0 };
}

// Back-lever variants side by side: no refinance · Variant A (proceeds to LPs) · Variant B (proceeds recycled into add-ons).
export function backLeverCompare(M) {
  const mk = (patch) => {
    const base = { ...M, ...patch };
    const MM = isRollupScope(M) ? rollupScopedModel({ ...base, addons: undefined }) : base;
    const rows = model5yr(MM);
    return { M: MM, rows, wf: lpWaterfall(MM), ex: exitReturns(MM), plan: cashPlan(MM, rows), bl: backLever(MM) };
  };
  return { off: mk({ blOn: false }), distribute: mk({ blOn: true, blUse: "distribute" }), addons: mk({ blOn: true, blUse: "addons" }) };
}

export function addonScopedModel(M, idx) {
  const R = { ...DEFAULT_ROLLUP, ...(M.rollup || {}) };
  const row = rollupModel(R).rows[idx];
  if (!row) return M;
  const price = row.price;
  return {
    ...M, _scope: `addon:${idx}`, dealName: row.name, companyName: row.name, txnType: "acquisition", recap: undefined, rollup: undefined, addons: undefined,
    sde: row.ebitda, askPrice: price, downPct: price > 0 ? ((row.equity + row.rollover) / price) * 100 : 0, snAmt: row.sellerNote, rollPct: Number(R.rolloverPct) || 0,
    rate: Number(R.debtRate) || M.rate, salary: 0, capex: 0, slbOn: false, slbProceeds: 0, reVal: 0, nwc: 0, ffe: 0, goodwill: 0, transExpenses: 0, historicalYears: [], revenueMix: [],
    ttmRevenue: M.sde > 0 ? Math.round((row.ebitda * (M.ttmRevenue || 0)) / M.sde) : 0, lpEquity: 0, cashPolicy: "distribute", platformRole: "addon",
  };
}

export function scopedModel(M, scope) {
  if (!scope || scope === SCOPE_PLATFORM || !scopeOptions(M)) return M;
  if (scope === SCOPE_ROLLUP) return rollupScopedModel(M);
  const idx = Number(String(scope).split(":")[1]);
  return Number.isNaN(idx) ? M : addonScopedModel(M, idx);
}

// ---------- deal-scout buy-box scoring ----------
// Overlay a scouted listing onto the current model assumptions and score it against the buy-box.
export function buyBoxScore(listing, M) {
  const price = Number(listing.askingPrice) || 0;
  const sde = Number(listing.ebitda) || 0;
  if (price <= 0 || sde <= 0) return null;
  const M2 = { ...M, askPrice: price, sde, snAmt: Math.min(M.snAmt, price * 0.15), ttmRevenue: Number(listing.revenue) || M.ttmRevenue };
  const dscr = dscrYr1(M2);
  const ret = exitReturns(M2);
  const irr = isNaN(ret.irr) ? 0 : ret.irr;
  const moic = ret.moic || 0;
  const multiple = sde > 0 ? price / sde : 0;
  const checks = {
    dscr: dscr >= 1.25,
    irr: irr >= 20,
    moic: moic >= 2.0,
    multiple: multiple > 0 && multiple <= 4.0,
  };
  const clears = checks.dscr && checks.irr && checks.moic;
  const score = Math.round(
    Math.min(100,
      Math.max(0, Math.min(dscr / 1.5, 1)) * 30 +
      Math.max(0, Math.min(irr / 30, 1)) * 30 +
      Math.max(0, Math.min(moic / 3, 1)) * 25 +
      (multiple > 0 ? Math.max(0, Math.min(4 / multiple, 1)) * 15 : 0)
    )
  );
  return { dscr, irr, moic, multiple, clears, score, checks };
}

// ---------- verdict ----------
export function revenueTrend(M) {
  const hy = M.historicalYears || [];
  if (hy.length < 2) return { declinePct: 0, growing: true };
  const rev = hy.map((h) => h.revenue);
  const peak = Math.max(...rev);
  const latest = rev[rev.length - 1];
  const declinePct = peak > 0 ? ((peak - latest) / peak) * 100 : 0;
  const growing = latest >= rev[rev.length - 2];
  return { declinePct, growing };
}

export function sdeQuality(M) {
  if (!M.sde) return 0;
  return (1 - (M.officerSalaryAddback || 0) / M.sde) * 100; // % NOT addbacks
}

// Reported gross margin: the assumption if set, else the latest historical year that carries a GM.
export function grossMargin(M) {
  const gm = pctOf(Number((M || {}).grossMarginPct) || 0);
  if (gm > 0) return gm;
  const hy = ((M || {}).historicalYears || []).filter((h) => Number(h?.gm) > 0);
  return hy.length ? pctOf(Number(hy[hy.length - 1].gm)) : 0;
}


// ---------- summary P&L ----------
// Reported years as a P&L. Revenue and gross margin are reported; adjusted EBITDA is the SDE row,
// so operating expenses are the implied plug (gross profit − EBITDA) and every column ties.
export function historicalPL(M) {
  const hy = (M || {}).historicalYears || [];
  return hy.map((h, i) => {
    const revenue = n_(h.revenue);
    const gmPct = pctOf(h.gm) || grossMargin(M);
    const gp = revenue * (gmPct / 100);
    // Parsed statements often omit adjusted earnings on the trailing year — fall back to the deal's
    // stated adjusted EBITDA there, the same way the deal room's TTM card does.
    const ebitda = n_(h.sde) || (i === hy.length - 1 ? n_(M.sde) : 0);
    return { label: String(h.year || ""), hist: true, revenue, cogs: revenue - gp, gp,
      gmPct: revenue ? (gp / revenue) * 100 : 0, opex: gp - ebitda, ebitda,
      ebitdaPct: revenue ? (ebitda / revenue) * 100 : 0, netIncome: n_(h.netIncome) };
  });
}

// Projected P&L for the active scope. Revenue compounds at the organic growth assumption and add-on
// revenue rides the platform's revenue-per-EBITDA ratio — the same basis model5yr uses — so the
// EBITDA row equals the pro-forma cash-flow EBITDA year for year. COGS and operating expenses are
// DERIVED by subtraction (COGS = Revenue − Gross profit; Opex = Gross profit − EBITDA) so every
// column ties exactly: Revenue − COGS = Gross profit, and Gross profit − Opex = EBITDA.
export function projectedPL(V) {
  const g = n_(V && V.sdeGrowth) / 100;
  const rev0 = n_(V && V.ttmRevenue);
  const ratioRev = V && V.sde > 0 ? rev0 / V.sde : 0;
  const gmPct = grossMargin(V);
  return model5yr(V).map((r) => {
    const revenue = rev0 * Math.pow(1 + g, r.y - 1) + ratioRev * r.addon_e;
    const gp = revenue * (gmPct / 100);
    return { label: `Y${r.y}`, hist: false, y: r.y, revenue, cogs: revenue - gp, gp, gmPct,
      opex: gp - r.sde_y, ebitda: r.sde_y, ebitdaPct: revenue ? (r.sde_y / revenue) * 100 : 0,
      rent: r.rent_y, ebitdaAfterRent: r.sde_y - r.rent_y,
      // The EBITDA→FCF bridge, every line visible, so the P&L ties to the pro forma cash flow on Returns.
      salary: n_(r.salary_y), capex: n_(r.capex_y), mgmt: n_(r.mgmt_y), cfads: n_(r.cfads),
      ds: n_(r.senior_y) + n_(r.sn_y) + n_(r.addon_ds), fcf: r.fcf };
  });
}

// ---------- blended EBITDA (weighted 3-year average) ----------
export const DEFAULT_BLEND_WEIGHTS = [50, 25, 25];
// Most recent full year first. Falls back to the last three historical rows (TTM included) when there are fewer than three full years.
export function blendSeed(M) {
  const hy = (M.historicalYears || []).filter((h) => h && (h.sde || h.revenue));
  const full = hy.filter((h) => !/ttm|ltm/i.test(String(h.year || "")));
  const src = (full.length >= 3 ? full : hy).slice(-3).reverse();
  return [0, 1, 2].map((i) => ({ label: src[i]?.year ? String(src[i].year) : `Year ${i + 1}`, value: Number(src[i]?.sde) || 0 }));
}
export function blendedEbitda(M) {
  const b = M.ebitdaBlend || {};
  const seed = blendSeed(M);
  const years = [0, 1, 2].map((i) => ({
    label: b.labels?.[i] || seed[i].label,
    value: b.values?.[i] != null ? Number(b.values[i]) || 0 : seed[i].value,
    weight: b.weights?.[i] != null ? Number(b.weights[i]) || 0 : DEFAULT_BLEND_WEIGHTS[i],
  }));
  const wSum = years.reduce((a, y) => a + y.weight, 0);
  const value = wSum > 0 ? years.reduce((a, y) => a + y.value * y.weight, 0) / wSum : 0;
  return { years, value, wSum, seeded: !b.values };
}

export function verdict(M) {
  const dscr = dscrYr1(M);
  const { declinePct, growing } = revenueTrend(M);
  const quality = sdeQuality(M);
  const gm = grossMargin(M);
  const te = trueEarnings(M);

  // Hard fails → PASS regardless of the rest
  if (dscr < 1.05 || M.sde <= 0 || te < 0 || declinePct > 20) return "PASS";

  // Weighted score across the core underwriting signals
  let score = 0;
  if (dscr >= 1.5) score += 2; else if (dscr >= 1.3) score += 1; else if (dscr < 1.15) score -= 1;
  if (growing || declinePct <= 0) score += 1; else if (declinePct > 10) score -= 1;
  if (quality >= 55) score += 1; else if (quality < 35) score -= 1;
  if (gm >= 35) score += 1; else if (gm > 0 && gm < 20) score -= 1;
  if (te > 0) score += 1;

  if (score >= 3) return "GO";
  if (score <= 0) return "PASS";
  return "CONDITIONAL GO";
}

export function verdictReasons(M) {
  const dscr = dscrYr1(M);
  const { declinePct, growing } = revenueTrend(M);
  const quality = sdeQuality(M);
  const gm = grossMargin(M);
  const te = trueEarnings(M);
  const pos = [], neg = [];
  if (dscr < 1.05) neg.push(`DSCR ${dscr.toFixed(2)}x below 1.05 — won't cover debt`);
  else if (dscr < 1.3) neg.push(`DSCR ${dscr.toFixed(2)}x is thin (under 1.30x)`);
  else pos.push(`DSCR ${dscr.toFixed(2)}x comfortably covers debt`);
  if ((M.sde || 0) <= 0) neg.push(`SDE is not positive`);
  if (te < 0) neg.push(`True earnings negative after a market salary & capex`);
  else if (te > 0 && (M.sde || 0) > 0) pos.push(`Positive true earnings of ${fmt$(te)}`);
  if (declinePct > 20) neg.push(`Revenue down ${declinePct.toFixed(0)}% — steep decline`);
  else if (declinePct > 0) neg.push(`Revenue declining ~${declinePct.toFixed(0)}%`);
  else if (growing) pos.push(`Revenue is growing`);
  if (gm >= 35) pos.push(`Healthy ${gm.toFixed(0)}% gross margin`);
  else if (gm > 0 && gm < 20) neg.push(`Low ${gm.toFixed(0)}% gross margin`);
  if (quality >= 55) pos.push(`Clean SDE (${quality.toFixed(0)}% quality, low add-backs)`);
  else if (quality < 35) neg.push(`Heavy owner add-backs (${quality.toFixed(0)}% SDE quality)`);
  const v = verdict(M);
  const ordered = v === "GO" ? [...pos, ...neg] : v === "PASS" ? [...neg, ...pos] : [...neg, ...pos];
  return ordered.slice(0, 3);
}

// ---------- scenarios ----------
export function scenarioModel(M, scen) {
  // The scenario flexes growth (which drives platform AND add-on EBITDA) and the exit multiple —
  // including the roll-up's own consolidated exit multiple.
  const withRollup = (MM, delta) => (MM.rollup
    ? { ...MM, rollup: { ...MM.rollup, exitMultiple: Math.max(1, (Number(MM.rollup.exitMultiple) || 0) + delta) } }
    : MM);
  if (scen === "upside") {
    const d = M.upExitDelta == null ? 0.75 : Number(M.upExitDelta) || 0;
    return withRollup({ ...M, sdeGrowth: M.upGrowth == null ? M.sdeGrowth + 3 : M.upGrowth, exitMult: M.exitMult + d }, d);
  }
  if (scen === "downside") {
    const d = M.downExitDelta == null ? 0.75 : Number(M.downExitDelta) || 0;
    return withRollup({ ...M, sdeGrowth: M.downGrowth == null ? M.sdeGrowth - 4 : M.downGrowth, exitMult: Math.max(1, M.exitMult - d) }, -d);
  }
  return { ...M };
}

// ---------- color helpers ----------
export const dscrTone = (d) => (d >= 1.3 ? "go" : d >= 1.1 ? "amber" : "pass");
export const moicTone = (m) => (m > 3 ? "go" : m >= 2 ? "amber" : m >= 1.2 ? "amber" : "pass");
export const fcfTone = (f) => (f >= 0 ? "go" : "pass");

export function sensDSCR(cell) {
  return cell >= 1.5 ? "s-hi" : cell >= 1.3 ? "s-md" : cell >= 1.1 ? "s-ok" : "s-lo";
}
export function sensMOIC(cell) {
  return cell > 3 ? "s-hi" : cell >= 2 ? "s-md" : cell >= 1.2 ? "s-ok" : "s-lo";
}
export function sensFCF(cell) {
  return cell > 100000 ? "s-hi" : cell >= 50000 ? "s-md" : cell >= 0 ? "s-ok" : "s-lo";
}
export function sensIRR(cell) {
  if (isNaN(cell)) return "s-lo";
  return cell >= 25 ? "s-hi" : cell >= 15 ? "s-md" : cell >= 8 ? "s-ok" : "s-lo";
}

// ---------------- platform add-on consolidation ----------------
export const annuity = (principal, ratePct, years) => {
  if (!principal || !years) return 0;
  const r = ratePct / 100 / 12, n = years * 12;
  return r ? (principal * r / (1 - Math.pow(1 + r, -n))) * 12 : principal / years;
};

export const BLANK_DEAL = (name) => ({
  ...DEMO_DEAL,
  dealName: name, companyName: "", industry: "", address: "", brokerName: "", brokerPhone: "", brokerListingId: "",
  askPrice: 0, ttmRevenue: 0, sde: 0, grossMarginPct: 0, salary: 0, capex: 0, nwc: 0, ffe: 0, goodwill: 0, reVal: 0,
  transExpenses: 0, snAmt: 0, slbOn: false, slbProceeds: 0, historicalYears: [], revenueMix: [], rollup: undefined,
  borrowingBase: undefined, platformRole: "standalone", platformId: null, synergyCost: 0, synergyRevPct: 0,
});

export function consolidation(M, P) {
  if (!P) return null;
  const targetEbitda = trueEarnings(M);
  const price = nac(M);
  const targetMult = targetEbitda > 0 ? price / targetEbitda : 0;
  const revSyn = (M.ttmRevenue || 0) * ((M.synergyRevPct || 0) / 100) * ((M.grossMarginPct || 0) / 100);
  const synergies = (M.synergyCost || 0) + revSyn;
  const combinedEbitda = (P.ebitda || 0) + targetEbitda + synergies;
  const platformEV = (P.ebitda || 0) * (P.multiplePaid || 0);
  const blendedMult = (P.ebitda || 0) + targetEbitda > 0 ? (platformEV + price) / ((P.ebitda || 0) + targetEbitda) : 0;
  const markMult = P.multiplePaid || M.exitMult || 0;
  const valueCreated = combinedEbitda * markMult - (platformEV + price);
  const platformDS = P.debtService || annuity(P.debt || 0, P.debtRate || M.rate, P.debtYears || M.loanYrs);
  const combinedDebt = (P.debt || 0) + loanAmt(M) + (M.snAmt || 0);
  const combinedDS = platformDS + totalAnnualDS(M);
  const leverage = combinedEbitda > 0 ? combinedDebt / combinedEbitda : 0;
  const dscr = combinedDS > 0 ? (combinedEbitda - (M.capex || 0)) / combinedDS : 0;
  const platformEquity = Math.max(0, platformEV - (P.debt || 0));
  const incEquity = buyerCashIn(M);
  const eqBefore = platformEquity > 0 ? (platformEV - (P.debt || 0)) / platformEquity : 0;
  const eqAfterVal = combinedEbitda * markMult - combinedDebt;
  const eqAfter = platformEquity + incEquity > 0 ? eqAfterVal / (platformEquity + incEquity) : 0;
  return { targetEbitda, price, targetMult, revSyn, synergies, combinedEbitda, platformEV, blendedMult, markMult, valueCreated,
    platformDS, combinedDebt, combinedDS, leverage, dscr, platformEquity, incEquity, impliedMoicBefore: eqBefore, impliedMoicAfter: eqAfter,
    accretion: eqAfter - eqBefore, arbitrage: markMult - targetMult };
}

// ---------- balanced Sources & Uses ----------
const U_WARCHEST = "Add-on war chest (cash to balance sheet)";
const U_EXCESS = "Excess proceeds to balance sheet";

function consolidatedSourcesUses(M) {
  const A = M.addons;                       // committed add-ons (+ back-lever synthetic)
  const sf = sfGroupRow(M);                 // self-funded buys: one line, paid from retained cash
  const all = consolidatedAddons(M);
  const plan = cashPlan(M);
  const base = sourcesUses({ ...M, addons: undefined });
  const sum = (k, rows = all) => rows.reduce((a, c) => a + (c[k] || 0), 0);
  const called = plan.totalCalled;
  const escrow = plan.fundedAtClose || 0;   // committed add-on equity the LPs fund at close, in one check
  const warChest = plan.warChest; // cash raised/left on the balance sheet for add-ons (incl. recap excess)
  const bl = plan.bl, refiPool = plan.refiToReserve;
  const warUsed = Math.min(warChest, plan.totalDeployed);
  const refiUsed = Math.min(refiPool, Math.max(0, plan.totalDeployed - warUsed));
  const cfUsed = Math.max(0, plan.totalDeployed - warUsed - refiUsed);
  // The war chest is already raised in the platform Sources — spending it on the add-on prices below
  // must replace those Uses lines, otherwise the same dollars are raised twice.
  const keptUses = base.uses.filter((u) => u[0] !== U_WARCHEST && u[0] !== U_EXCESS);
  const droppedUses = base.totalUses - keptUses.reduce((a, u) => a + u[1], 0);
  const unspent = Math.max(0, warChest - warUsed);
  const unspentRefi = Math.max(0, refiPool - refiUsed);
  const addonEbitda = A.reduce((a, c) => a + (c.multiple > 0 ? (c.price || 0) / c.multiple : 0), 0);
  const blended = addonEbitda > 0 ? sum("price", A) / addonEbitda : 0;
  const yrs = A.map((c) => c.year).filter((y) => y > 0);
  const yrLabel = yrs.length ? (Math.min(...yrs) === Math.max(...yrs) ? `Yr ${yrs[0]}` : `Yr ${Math.min(...yrs)}–${Math.max(...yrs)}`) : "";
  const addonLine = A.length === 1
    ? `Add-on · ${A[0].name} (Yr ${A[0].year} · ${fmtX(A[0].multiple)})`
    : `Committed add-ons · ${A.length} (${yrLabel} · ${fmtX(blended)})`;
  const sfLine = sf ? [`Self-funded add-ons · ${sf.count} (${sf.yearLabel} · ${fmtX(sf.multiple)})`, sf.price] : null;
  const uses = [...keptUses, [addonLine, sum("price", A)], ...(sfLine ? [sfLine] : []),
    ["Add-on working capital funded at close", sum("nwc")],
    ["Add-on transaction costs (fees)", sum("fees")],
    ["Cash left on balance sheet (unspent war chest)", unspent],
    ["Cash left on balance sheet (unspent refinance proceeds)", unspentRefi]].filter((u) => Math.abs(u[1]) > 0.5);
  const totalUses = base.totalUses - droppedUses + sum("price") + sum("nwc") + sum("fees") + unspent + unspentRefi;
  const LP_LINES = new Set(["Sponsor / investor equity", "New LP equity"]);
  const baseSources = base.sources.map((u) => (LP_LINES.has(u[0]) && escrow > 0.5 ? [u[0], u[1] + escrow] : u));
  if (escrow > 0.5 && !base.sources.some((u) => LP_LINES.has(u[0]))) baseSources.push([isRecap(M) ? "New LP equity" : "Sponsor / investor equity", escrow]);
  const sources = [...baseSources, ["Add-on senior debt", sum("debt")], ["Add-on seller notes", sum("sellerNote")], ["Add-on seller rollover", sum("rollover")],
    [`Back-lever refinance proceeds (Yr ${bl ? bl.year : 0})`, refiPool],
    ["Retained cash flow — self-funded add-ons", cfUsed], ["LP capital calls — add-on equity", called]].filter((u) => u[1] > 0.5);
  const totalSources = sources.reduce((a, u) => a + u[1], 0);
  return { ...base, uses, totalUses, sources, totalSources, equity: base.equity + called + escrow, gap: totalUses - totalSources, platform: base, addons: A, sf,
    addonPrice: sum("price", A), addonDebt: sum("debt"), addonSN: sum("sellerNote"), addonRollover: sum("rollover"), deployed: plan.totalDeployed, warChest, warUsed, unspent, refiPool, refiUsed, unspentRefi, called, escrow, lpAddonEquity: called + escrow };
}

export const DEBT_SOURCE_LABELS = new Set(["Senior debt", "New senior facility", "Seller note", "Seller / legacy note", "Add-on senior debt", "Add-on seller notes"]);

// Every equity dollar in the deal (everything in Sources that is not debt) — the denominator the
// Sources — Equity table shows as "Total Equity", and the basis for rolled-stake ownership.
export const equitySourcesTotal = (M) =>
  sourcesUses(M).sources.filter(([d]) => !DEBT_SOURCE_LABELS.has(d)).reduce((s, [, a]) => s + (a || 0), 0);

export function sourcesUses(M) {
  if (isRollupScope(M)) return consolidatedSourcesUses(M);
  if (isRecap(M)) {
    const r = recapModel(M);
    return { uses: r.uses, totalUses: r.totalUses, sources: r.sources, totalSources: r.totalSources, equity: r.newLp, gap: r.totalUses - r.totalSources, recap: r };
  }
  const price = M.askPrice || 0;
  const alloc = (M.reVal || 0) + (M.nwc || 0) + (M.ffe || 0);
  const goodwill = M.goodwill > 0 ? M.goodwill : Math.max(0, price - alloc);
  const plug = price - (goodwill + alloc);
  const fees = transCostsTotal(M);
  const uses = [
    ["Goodwill / equity purchase", goodwill], ["Real estate", M.reVal || 0], ["NWC / inventory", M.nwc || 0], ["FF&E", M.ffe || 0],
    [plug > 0 ? "Other purchase price allocation" : "Purchase price adjustment", plug],
    ["Transaction expenses", M.transExpenses || 0], ["Transaction costs (fees)", fees],
  ].filter((u) => Math.abs(u[1]) > 0.5);
  const totalUses = price + (M.transExpenses || 0) + fees;
  const slb = M.slbOn ? M.slbProceeds || 0 : 0, loan = loanAmt(M), sn = M.snAmt || 0, roll = rolloverAmt(M);
  const financed = slb + loan + sn + roll;
  const equity = Math.max(0, totalUses - financed);
  // Sale-leaseback / financing proceeds above the funding need are not free money — they stay on the
  // balance sheet as cash, so they must appear as a Use or Sources would overstate the deal.
  const surplus = Math.max(0, financed - totalUses);
  const usesOut = surplus > 0.5 ? [...uses, [U_EXCESS, surplus]] : uses;
  const totalUsesOut = totalUses + surplus;
  const sources = [["SLB proceeds", slb], ["Senior debt", loan], ["Seller note", sn], ["Sponsor / investor equity", equity], ["Seller rollover", roll]].filter((u) => u[1] > 0);
  const totalSources = sources.reduce((s, u) => s + u[1], 0);
  return { uses: usesOut, totalUses: totalUsesOut, sources, totalSources, equity, surplus, gap: totalUsesOut - totalSources };
}
